import { grpc } from '@improbable-eng/grpc-web';
import { config } from '../env';
import { UserManagerClient } from '../proto/user-manager_pb_service';
import {
  AddUserReq,
  AnnouncementSurfaceMap,
  EditUserReq,
  GetMyAnnouncementStateReply,
  GetMyAnnouncementStateReq,
  GetMyEarlyAccessReply,
  GetMyEarlyAccessReq,
  MarkAnnouncementsReq,
  GetUserInfoReq,
  HasQueuedEditEmailReq,
  ListConnectedAppsReq,
  ListConnectedAppsReply,
  MarkTourCompletedReq,
  RevokeConnectedAppReq,
  SubscribeToBetaReq,
  UpdateEmailReq,
  UpdatePasswordReq
} from '../proto/user-manager_pb';
import { AccountManagerClient } from '../proto/account-manager_pb_service';
import {
  AccountListReq,
  AddAccountReq,
  AutoMapSymbolsReq,
  ChangeAccountEnvReq,
  CloseAccountsTradesReq,
  CompareSymbolsReq,
  DeleteAccountReq,
  EnableAccountReq,
  EnableAccountSymbolReq,
  EnableAllAccountSymbolsReq,
  GetAccountInfoReq,
  GetEPSettingsReq,
  GetMcpSettingsReq,
  UpsertMcpSettingsReq,
  GetMcpAuditLogsReq,
  ListIndicatorScriptsReq,
  GetIndicatorScriptReq,
  CreateIndicatorScriptReq,
  UpdateIndicatorScriptReq,
  DeleteIndicatorScriptReq,
  GroupListReq,
  GetUsersDedicatedEnvListReq,
  ListAccountSymbolsReq,
  ListCopiedTradesReq,
  CopiedTradeLogsReq,
  MappedSymbolListReq,
  SymbolStatsReq,
  UnmappedSymbolListReq,
  UpdateAccountReq,
  UpsertEPSettingsReq,
  CheckAvailableEnvReq,
  UpdateDedicatedEnvReq,
  GetAllowedCTraderAccountsReq,
  SaveCTraderAuthInfoReq,
  GetCTraderAuthenticatedProfileListReq,
  UpdateCTraderAuthInfoReq,
  CTraderInfoReq,
  GetDxtUsersAccountListReq,
  DxtInfoReq,
  TradeLockerInfoReq,
  GetTradeLockerUsersAccountListReq,
  TimelyReset,
  GetMatchTraderUsersAccountListReq,
  MatchTreaderInfoReq,
  OpenTradeWarningReq,
  ActivateBetaConnectionReq,
  ActivateAnalyzerReq,
  SymbolLotOverrideReq,
  DeleteSymbolLotOverrideReq,
  SaveTradovateAuthInfoReq,
  GetTradovateProfileListReq,
  UpdateTradovateAuthInfoReq,
  GetTradovateUserAccountListReq,
  TradovateInfoReq,
  DeleteCTraderAuthInfoReq,
  DeleteTradovateAuthInfoReq,
  GetProjectxUserAccountListReq,
  ProjectXReq,
  GetRithmicUserAccountListReq,
  RithmicReq,
  ListAccountExchangesReq,
  ListExchangeProductsReq,
  ImportExchangeSymbolsForAccountReq,
  GetUsersRithmicProfilesReq,
  ChangeProfileEnvReq
} from '../proto/account-manager_pb';
import { TradeCopierClient } from '../proto/trade-copier_pb_service';
import {
  AddConfigReq,
  AddCopierTemplateReq,
  AddSlavesReq,
  AddSymbolMappingReq,
  AssociatedMasterSymbolReq,
  CopyExistingTradeReq,
  DeleteConfigReq,
  DeleteCopierTemplateReq,
  DeleteSalvesReq,
  DeleteSymbolMappingReq,
  DeleteSymbolSettingsReq,
  EditSymbolMappingReq,
  GetUserConfigsReq,
  ListCopierTemplateReq,
  ListSymbolMappingsReq,
  ListSymbolSettingsReq,
  MappingReq,
  MasterOrderInfo,
  PauseAccountReq,
  PauseAccountSymbolReq,
  SimulateTradeReq,
  SimulateTradeReply,
  TradingHour,
  UpdateConfigReq,
  UpdateCopierTemplateReq,
  UpdatedSlaveConfig,
  UpdateSlaveManyReq,
  UpdateSlaveReq,
  UpdateSymbolSettingsReq
} from '../proto/trade-copier_pb';
import { BoolValue, DoubleValue, Int32Value, Int64Value, StringValue } from 'google-protobuf/google/protobuf/wrappers_pb';
import { BrokerManagerClient } from '../proto/broker-manager_pb_service';
import { GetBrokerInfoReq, GetServerInfoReq, ListBrokersReq } from '../proto/broker-manager_pb';
import { BillingManagerClient } from '../proto/billing-manager_pb_service';
import { readObref, readOppref } from 'utils/oaiq';

import {
  AddSubscriptionItemReq,
  BillingOverviewReq,
  CancelSubscriptionImmediatelyReq,
  CancelSubscriptionReq,
  CardSetUpIntentReq,
  CreateSubscriptionFreezeReq,
  CreateCryptoPaymentRequest,
  AdAttribution,
  CreateSubscriptionReq,
  DeleteCardReq,
  GetDownloadableInvoiceReq,
  GetSubscriptionFreezeEligibilityReq,
  CancelCryptoPaymentRequest,
  GetOverDueInfoReq,
  GetSubscriptionFreezeReq,
  GetUpcomingInvoiceReq,
  HasPendingCryptoPaymentRequest,
  ListCardsReq,
  ListInvoicesReq,
  ListPricesReq,
  ListSubscriptionsReq,
  ModifySubscriptionReq,
  NewSubscriptionPreview,
  PreviewSubscriptionFreezeReq,
  PreviewCheckoutReq,
  SubscriptionUpdate,
  ReactivateSubscriptionReq,
  RemoveSubscriptionItemReq,
  CancelSubscriptionFreezeReq,
  GetCancelOfferReq,
  AcceptCancelOfferReq,
  ResumeSubscriptionFreezeReq,
  RenewOverDueSubscriptionReq,
  SetDefaultPaymentMethodReq,
  SubscriptionItem,
  SubscriptionItemDelta
} from '../proto/billing-manager_pb';
import { AnalyticsManagerClient } from '../proto/analytics-manager_pb_service';
import {
  AccountsOverviewStatsReq,
  AccountStatsReq,
  CopierMetricsReq,
  GetBalanceInfoReq,
  ListAccountStatsReq,
  ListTradesReq,
  OverviewStatsReq
} from '../proto/analytics-manager_pb';
import { NewsManagerClient } from '../proto/news-manager_pb_service';
import {
  CalendarEventListReq,
  CreateCalendarEventAlertReply,
  CreateCalendarEventAlertReq,
  DeleteCalendarEventAlertReply,
  DeleteCalendarEventAlertReq,
  ListMyAlertsReply,
  ListMyAlertsReq,
  MarketNewsListReq,
  ToggleNewsBookmarkReply,
  ToggleNewsBookmarkReq
} from '../proto/news-manager_pb';
import { NotificationManagerClient } from '../proto/notification-manager_pb_service';
import {
  AccountMeta,
  AddBotReq,
  AddChannelReq,
  AddTelegramRouteReq,
  BlockNotificationReq,
  ChannelMeta,
  ConfigDetailsReq,
  DeleteBotReq,
  DeleteChannelReq,
  DeleteTelegramRouteReq,
  DisableNotificationReq,
  EditBotEventDetailsReq,
  EditBotEventStatusReq,
  EditBotReq,
  EditBotStatusReq,
  EditChannelReq,
  EditChannelStatusReq,
  EditConfigReq,
  EditNotificationEmailReq,
  EditTelegramRouteStatusReq,
  EnableNotificationReq,
  GetBotEventConfigsReq,
  GetBotEventDetailsReq,
  GetBotsReq,
  GetChannelReq,
  GetNotificationReq,
  GetNotificationsReq,
  GetTelegramRoutesReq,
  InstallIntegrationReq,
  IntegrationStatusReq,
  GetBotChannelLinksReq,
  LinkBotToChannelReq,
  NotificationConfig,
  OverrideNotificationReq,
  UnblockNotificationReq,
  UnlinkBotFromChannelReq
} from '../proto/notification-manager_pb';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { StatusManagerClient } from '../proto/status_manager_pb_service';
import { StatusReq } from 'proto/status_manager_pb';
import { risk_type_display_map } from 'sections/trade-copier/CopyTradingTableSection';
import { SentimentAnalyzerClient } from 'proto/sentiment-analyzer_pb_service';
import { GetBrokersSymbolSentimentReq, GetSymbolsReq, GetSymbolsSentimentReq, SentimentOverviewReq } from 'proto/sentiment-analyzer_pb';
import { AnalyzerClient } from 'proto/analyzer_pb_service';
import {
  GetAccountTradeListRequest,
  ListAccountsReq,
  ListAccountsLiteReq,
  GetPerformanceOverviewReq,
  GetAccountPerformanceRequest,
  GetPortfolioOverviewReq,
  GetPortfolioEquityCurveReq,
  GetPortfolioDailyAndCumulativeROIReq,
  GetUserTagsReq,
  CreateUserTagReq,
  DeleteUserTagReq,
  SetTradeTagReq,
  UpdateUserTagReq,
  GetAccountSettingsReq,
  UpdateAccountSettingsReq,
  AccountAlertSettings as AccountAlertSettingsProto
} from 'proto/analyzer_pb';
import { DateRange } from 'proto/common/common_pb';
import { FeatureRequestClient as FeatureRequestProtoClient } from '../proto/feature-request_pb_service';
import {
  CreatePostReq,
  ListPostsReq,
  GetPostReq,
  VoteReq,
  RemoveVoteReq,
  AddCommentReq,
  ListCommentsReq,
  DeleteCommentReq
} from '../proto/feature-request_pb';
import { TenantManagerClient } from '../proto/tenant-manager_pb_service';
import {
  Branding,
  GetMyTenantReq,
  AccountReleaseModeMap,
  GetMyTenantsReq,
  GetSeatUsageReq,
  ListTenantDedicatedEnvsReq,
  SetRoomDedicatedEnvReq,
  SetRoomSeatLimitsReq,
  UpdateBrandingReq,
  UploadBrandingAssetReq
} from '../proto/tenant-manager_pb';
import { EAHostingServiceClient as EAHostingServiceProtoClient } from '../proto/ea-hosting_pb_service';
import {
  UploadEARequest,
  UploadEAResponse,
  UploadSettingsSetRequest,
  SettingsSchemaResult,
  StartSetupJobRequest,
  StartSetupJobResponse,
  GetSettingsSchemaRequest,
  GetSettingsSchemaResponse,
  CreateBrokerProfileRequest,
  BrokerProfile,
  ListBrokerProfilesRequest,
  ListBrokerProfilesResponse,
  ValidateBrokerLoginRequest,
  ValidateBrokerLoginResponse,
  GetBrokerProfileSymbolsRequest,
  GetBrokerProfileSymbolsResponse,
  UpdateBrokerProfileRequest,
  DeleteBrokerProfileRequest,
  CreateInstanceRequest,
  Instance,
  ListInstancesRequest,
  ListInstancesResponse,
  DeleteInstanceRequest,
  StartInstanceRequest,
  StopInstanceRequest,
  RestartInstanceRequest,
  UpdateSettingsRequest,
  RollbackSettingsRequest,
  ListSettingsVersionsRequest,
  GetInstanceStatusRequest,
  GetInstanceJournalRequest,
  GetInstanceJournalResponse,
  GetInstanceMetricsRequest,
  InstanceMetrics,
  ListEquityHistoryRequest,
  ListEquityHistoryResponse,
  InstanceStatus,
  GetHostingOverviewRequest,
  ListUploadsRequest,
  ListUploadsResponse,
  DismissUploadRequest,
  HostingOverview
} from '../proto/ea-hosting_pb';
import { Struct } from 'google-protobuf/google/protobuf/struct_pb';
import { AchievementManagerClient } from '../proto/achievement-manager_pb_service';
import { GenerateCertificateReply, GenerateCertificateReq, TimeRangeMap } from '../proto/achievement-manager_pb';

/**
 * Builds the ad-attribution message for a create request.
 *
 * Falls back to reading the live click id when the caller supplied nothing,
 * which is every call site today. Returns undefined when there is no
 * attribution at all, so the field stays absent on the wire rather than
 * shipping an empty message on every organic purchase.
 */
function buildAdAttribution(supplied?: AdAttribution.AsObject): AdAttribution | undefined {
  const oppref = supplied?.oppref ?? readOppref();
  const obref = supplied?.obref ?? readObref();
  if (!oppref && !obref) return undefined;
  const attribution = new AdAttribution();
  attribution.setOppref(oppref);
  attribution.setObref(obref);
  return attribution;
}

let __userClient: UserManagerClient;
let __accountClient: AccountManagerClient;
let __tradeManagerClient: TradeCopierClient;
let __brokerManagerClient: BrokerManagerClient;
let __billingClient: BillingManagerClient;
let __analyticsManagerClient: AnalyticsManagerClient;
let __newsManagerClient: NewsManagerClient;
let __notificationClient: NotificationManagerClient;
let __statusManagerClient: StatusManagerClient;
let __sentimentClient: SentimentAnalyzerClient;
let __analyzerClient: AnalyzerClient;
let __featureRequestProtoClient: FeatureRequestProtoClient;
let __tenantClient: TenantManagerClient;
let __eaHostingProtoClient: EAHostingServiceProtoClient;
let __achievementClient: AchievementManagerClient;

const gatewayAddress = config.envoy_hostname;

/**
 * Makes selected keys of a generated `AsObject` optional. protoc marks every field
 * required, but these request builders default the ones a caller omits — so the
 * request type should say so instead of forcing callers to pass filler values.
 */
export type OptionalFields<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

type CreateSubscriptionRequest = OptionalFields<CreateSubscriptionReq.AsObject, 'coupon' | 'useTrial' | 'itemsList' | 'adAttribution'>;
type ModifySubscriptionRequest = OptionalFields<ModifySubscriptionReq.AsObject, 'subscriptionItemId' | 'priceId'>;
export type AccountTradeListRequest = OptionalFields<GetAccountTradeListRequest.AsObject, 'page' | 'search'>;

type CryptoPaymentRequest = Omit<CreateCryptoPaymentRequest.AsObject, 'createSubscription' | 'modifySubscription'> & {
  createSubscription?: CreateSubscriptionRequest;
  modifySubscription?: ModifySubscriptionRequest;
};

/** An absent bound stays absent: grpc-web reads null as "field not set". */
const toTimestamp = (value: Timestamp.AsObject | undefined): Timestamp => {
  if (value === null || value === undefined) return null as any;
  const timestamp = new Timestamp();
  timestamp.setSeconds(value.seconds);
  timestamp.setNanos(value.nanos);
  return timestamp;
};

export class TenantClient {
  meta: grpc.Metadata.HeaderObject = {};
  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__tenantClient != null) {
      return __tenantClient;
    }
    __tenantClient = new TenantManagerClient(gatewayAddress);
    return __tenantClient;
  };

  getMyTenants = () => {
    return new Promise<any>((resolve, reject) => {
      const req = new GetMyTenantsReq();
      this.getClient().getMyTenants(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  getMyTenant = (tenantId: string) => {
    return new Promise<any>((resolve, reject) => {
      const req = new GetMyTenantReq();
      req.setTenantId(tenantId);
      this.getClient().getMyTenant(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  getSeatUsage = (tenantId: string) => {
    return new Promise<any>((resolve, reject) => {
      const req = new GetSeatUsageReq();
      req.setTenantId(tenantId);
      this.getClient().getSeatUsage(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  // releaseMode is required by the server only when the new limits take seats members
  // hold — it says what becomes of the accounts on those seats.
  setRoomSeatLimits = (p: {
    tenantId: string;
    accountLimit: number;
    premAccLimit: number;
    futuresAccLimit: number;
    releaseMode?: AccountReleaseModeMap[keyof AccountReleaseModeMap];
  }) => {
    return new Promise<any>((resolve, reject) => {
      const req = new SetRoomSeatLimitsReq();
      req.setTenantId(p.tenantId);
      req.setAccountLimit(p.accountLimit);
      req.setPremAccLimit(p.premAccLimit);
      req.setFuturesAccLimit(p.futuresAccLimit);
      if (p.releaseMode) req.setReleaseMode(p.releaseMode);
      this.getClient().setRoomSeatLimits(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  listTenantDedicatedEnvs = (tenantId: string) => {
    return new Promise<any>((resolve, reject) => {
      const req = new ListTenantDedicatedEnvsReq();
      req.setTenantId(tenantId);
      this.getClient().listTenantDedicatedEnvs(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  setRoomDedicatedEnv = (p: { tenantId: string; dedicatedEnvId: string; accountLimit: number }) => {
    return new Promise<any>((resolve, reject) => {
      const req = new SetRoomDedicatedEnvReq();
      req.setTenantId(p.tenantId);
      req.setDedicatedEnvId(p.dedicatedEnvId);
      req.setAccountLimit(p.accountLimit);
      this.getClient().setRoomDedicatedEnv(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  updateBranding = (p: {
    tenantId: string;
    branding: { roomName: string; logoUrl: string; faviconUrl: string; poweredByEnabled: boolean };
  }) => {
    return new Promise<any>((resolve, reject) => {
      const req = new UpdateBrandingReq();
      req.setTenantId(p.tenantId);
      const b = new Branding();
      b.setRoomName(p.branding.roomName);
      b.setLogoUrl(p.branding.logoUrl);
      b.setFaviconUrl(p.branding.faviconUrl);
      b.setPoweredByEnabled(p.branding.poweredByEnabled);
      req.setBranding(b);
      this.getClient().updateBranding(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  uploadBrandingAsset = (p: {
    tenantId: string;
    assetType: Parameters<UploadBrandingAssetReq['setAssetType']>[0];
    contentType: string;
    content: Uint8Array;
  }) => {
    return new Promise<any>((resolve, reject) => {
      const req = new UploadBrandingAssetReq();
      req.setTenantId(p.tenantId);
      req.setAssetType(p.assetType);
      req.setContentType(p.contentType);
      req.setContent(p.content);
      this.getClient().uploadBrandingAsset(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };
}

export class UserClient {
  meta: grpc.Metadata.HeaderObject = {};
  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__userClient != null) {
      return __userClient;
    }
    __userClient = new UserManagerClient(gatewayAddress);
    return __userClient;
  };

  addUser = (user: AddUserReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddUserReq();
      req.setFirstName(user.firstName);
      req.setLastName(user.lastName);
      req.setEmail(user.email);
      req.setAddress(user.address);
      req.setBio(user.bio);
      req.setCountry(user.country);
      req.setPhone(user.phone);
      req.setTimezone(user.timezone);

      const client = this.getClient();
      client.addUser(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  editUser = (user: EditUserReq.AsObject) => {
    return new Promise((resolve, reject) => {
      console.log('UserClient.editUser calling with:', user);
      let req = new EditUserReq();
      req.setFirstName(user.firstName);
      req.setLastName(user.lastName);
      // req.setEmail(user.email);
      req.setAddress(user.address);
      req.setBio(user.bio);
      req.setCountry(user.country);
      req.setPhone(user.phone);
      req.setUserId(user.userId);
      req.setCurrency(user.currency);
      req.setTimezone(user.timezone);

      const client = this.getClient();
      client.editUser(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateEmail = (user: UpdateEmailReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateEmailReq();
      req.setEmail(user.email);
      req.setUserId(user.userId);

      const client = this.getClient();
      client.updateEmail(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  hasQueuedEditEmail = (user: HasQueuedEditEmailReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new HasQueuedEditEmailReq();
      req.setUserId(user.userId);

      const client = this.getClient();
      client.hasQueuedEditEmail(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getUserInfo = (getUserReq: GetUserInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetUserInfoReq();
      req.setUserId(getUserReq.userId);

      const client = this.getClient();
      client.getUserInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updatePassword = (updateReq: UpdatePasswordReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdatePasswordReq();
      req.setUserId(updateReq.userId);
      req.setNewPassword(updateReq.newPassword);

      const client = this.getClient();
      client.updatePassword(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  subscribeToBeta = (betaReq: SubscribeToBetaReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SubscribeToBetaReq();
      req.setUserId(betaReq.userId);
      req.setSubscribe(betaReq.subscribe);

      const client = this.getClient();
      client.subscribeToBeta(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  markTourCompleted = () => {
    return new Promise((resolve, reject) => {
      const client = this.getClient();
      client.markTourCompleted(new MarkTourCompletedReq(), this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  listConnectedApps = (userId: string): Promise<ListConnectedAppsReply.AsObject> => {
    return new Promise((resolve, reject) => {
      let req = new ListConnectedAppsReq();
      req.setUserId(userId);

      const client = this.getClient();
      client.listConnectedApps(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  // The caller's early-access sign-ups: program, status, and when they joined, got access and first bought.
  getMyEarlyAccess = (userId: string): Promise<GetMyEarlyAccessReply.AsObject> => {
    return new Promise((resolve, reject) => {
      let req = new GetMyEarlyAccessReq();
      req.setUserId(userId);

      const client = this.getClient();
      client.getMyEarlyAccess(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  // What's New: the caller's sign-up date and where they've already seen each post.
  getMyAnnouncementState = (userId: string): Promise<GetMyAnnouncementStateReply.AsObject> => {
    return new Promise((resolve, reject) => {
      let req = new GetMyAnnouncementStateReq();
      req.setUserId(userId);

      const client = this.getClient();
      client.getMyAnnouncementState(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  // Records that the caller saw these posts in one place; repeats are ignored server-side.
  markAnnouncements = (userId: string, postIds: string[], surface: AnnouncementSurfaceMap[keyof AnnouncementSurfaceMap]): Promise<void> => {
    return new Promise((resolve, reject) => {
      let req = new MarkAnnouncementsReq();
      req.setUserId(userId);
      req.setPostIdsList(postIds);
      req.setSurface(surface);

      const client = this.getClient();
      client.markAnnouncements(req, this.meta as any, (err) => {
        if (err != null) {
          reject(err);
        } else {
          resolve();
        }
      });
    });
  };

  revokeConnectedApp = (userId: string, grantId: string) => {
    return new Promise((resolve, reject) => {
      let req = new RevokeConnectedAppReq();
      req.setUserId(userId);
      req.setGrantId(grantId);

      const client = this.getClient();
      client.revokeConnectedApp(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };
}

export class AccountClient {
  meta: grpc.Metadata.HeaderObject = {};
  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__accountClient != null) {
      return __accountClient;
    }
    __accountClient = new AccountManagerClient(gatewayAddress);
    return __accountClient;
  };

  addAccount = (accountReq: AddAccountReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddAccountReq();
      req.setAccountNumber(accountReq.accountNumber);
      req.setHost(accountReq.host);
      req.setPort(accountReq.port);
      req.setUserId(accountReq.userId);
      req.setPassword(accountReq.password);
      req.setNickname(accountReq.nickname);
      req.setPlatform(accountReq.platform);
      req.setBrokerId(accountReq.brokerId);
      req.setServerId(accountReq.serverId);
      req.setPriority(accountReq.priority);
      if (accountReq?.dxtInfo) {
        req.setDxtInfo(this.fromDxtInfo(accountReq?.dxtInfo));
      }
      if (accountReq.ctraderInfo) {
        req.setCtraderInfo(this.fromCTraderReqInfo(accountReq.ctraderInfo));
      }
      if (accountReq.tradeLockerInfo) {
        req.setTradeLockerInfo(this.fromTradeLockerReqInfo(accountReq.tradeLockerInfo));
      }
      if (accountReq.matchTraderInfo) {
        req.setMatchTraderInfo(this.fromMTRReqInfo(accountReq.matchTraderInfo));
      }
      if (accountReq.tradovateInfo) {
        req.setTradovateInfo(this.fromTVReqInfo(accountReq.tradovateInfo));
      }
      if (accountReq.projectxInfo) {
        req.setProjectxInfo(this.fromProjectXReqInfo(accountReq.projectxInfo));
      }
      if (accountReq.rithmicReq) {
        req.setRithmicReq(this.fromRithmicReqInfo(accountReq.rithmicReq));
      }
      req.setDedicatedEnvId(this.fromStingValueWithNull(accountReq.dedicatedEnvId));

      const client = this.getClient();
      client.addAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getDxtUsersAccountList = (accountReq: GetDxtUsersAccountListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetDxtUsersAccountListReq();
      req.setUserId(accountReq.userId);
      req.setHost(accountReq.host);
      req.setUsername(accountReq.username);
      req.setPassword(accountReq.password);

      const client = this.getClient();
      client.getDxtUsersAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getTradeLockerUsersAccountList = (accountReq: GetTradeLockerUsersAccountListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetTradeLockerUsersAccountListReq();
      req.setUserId(accountReq.userId);
      req.setEmail(accountReq.email);
      req.setServerName(accountReq.serverName);
      req.setIsDemo(accountReq.isDemo);
      req.setPassword(accountReq.password);

      const client = this.getClient();
      client.getTradeLockerUsersAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getMatchTraderUsersAccountList = (accountReq: GetMatchTraderUsersAccountListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetMatchTraderUsersAccountListReq();
      req.setUserId(accountReq.userId);
      req.setEmail(accountReq.email);
      req.setPassword(accountReq.password);
      req.setHost(accountReq.host);
      req.setBrokerId(accountReq.brokerId);

      const client = this.getClient();
      client.getMatchTraderUsersAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getProjectxUserAccountList = (accountReq: GetProjectxUserAccountListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetProjectxUserAccountListReq();
      req.setUserId(accountReq.userId);
      req.setHost(accountReq.host);
      req.setUsername(accountReq.username);
      req.setApiKey(accountReq.apiKey);

      const client = this.getClient();
      client.getProjectxUserAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getRithmicUserAccountList = (accountReq: GetRithmicUserAccountListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetRithmicUserAccountListReq();
      req.setUserId(accountReq.userId);
      req.setRithmicUser(accountReq.rithmicUser);
      req.setPassword(accountReq.password);
      req.setHost(accountReq.host);
      req.setServerName(accountReq.serverName);

      const client = this.getClient();
      client.getRithmicUserAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listAccountExchanges = (exchangesReq: ListAccountExchangesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListAccountExchangesReq();
      req.setUserId(exchangesReq.userId);
      req.setAccountId(exchangesReq.accountId);

      const client = this.getClient();
      client.listAccountExchanges(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listExchangeProducts = (productsReq: ListExchangeProductsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListExchangeProductsReq();
      req.setUserId(productsReq.userId);
      req.setAccountId(productsReq.accountId);
      req.setExchange(productsReq.exchange);
      req.setExchangeId(productsReq.exchangeId);

      const client = this.getClient();
      client.listExchangeProducts(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  importExchangeSymbolsForAccount = (importReq: ImportExchangeSymbolsForAccountReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ImportExchangeSymbolsForAccountReq();
      req.setUserId(importReq.userId);
      req.setAccountId(importReq.accountId);
      req.setExchange(importReq.exchange);
      req.setInstrumentType(importReq.instrumentType);
      req.setProductCode(importReq.productCode);

      const client = this.getClient();
      client.importExchangeSymbolsForAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateAccount = (accountReq: UpdateAccountReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateAccountReq();
      req.setAccountId(accountReq.accountId);
      req.setUserId(accountReq.userId);
      if (accountReq.nickname) {
        req.setNickname(accountReq.nickname);
      }
      if (accountReq.password) {
        req.setPassword(this.fromStingValue(accountReq.password));
      }
      if (accountReq.considerOnPortfolio?.value !== undefined) {
        req.setConsiderOnPortfolio(this.fromBoolValue(accountReq.considerOnPortfolio) as any);
      }
      if (accountReq.activatePremium?.value !== undefined) {
        req.setActivatePremium(this.fromBoolValue(accountReq.activatePremium) as any);
      }

      const client = this.getClient();
      client.updateAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getAccountList = (
    // Platform filter is optional and additive — existing callers pass only the
    // original fields; AI Connect adds platformsList.
    accountListReq: Omit<AccountListReq.AsObject, 'platformsList' | 'excludePlatforms'> & {
      platformsList?: string[];
      excludePlatforms?: boolean;
    }
  ) => {
    return new Promise((resolve, reject) => {
      let req = new AccountListReq();
      req.setUserId(accountListReq.userId);
      req.setPageSize(accountListReq.pageSize);
      req.setPaginationCursor(accountListReq.paginationCursor);
      req.setSearchPattern(accountListReq.searchPattern);
      // Optional platform filter (AI Connect sends MT-4/MT-5). Empty leaves the list unfiltered.
      if (accountListReq.platformsList?.length) {
        req.setPlatformsList([...accountListReq.platformsList]);
      }
      if (accountListReq.excludePlatforms) {
        req.setExcludePlatforms(true);
      }

      const client = this.getClient();
      client.getAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getAccountInfo = (accountInfoReq: GetAccountInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetAccountInfoReq();
      req.setUserId(accountInfoReq.userId);
      req.setAccountId(accountInfoReq.accountId);

      const client = this.getClient();
      client.getAccountInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteAccount = (deleteAccountReq: DeleteAccountReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteAccountReq();
      req.setUserId(deleteAccountReq.userId);
      req.setAccountId(deleteAccountReq.accountId);

      const client = this.getClient();
      client.deleteAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  enableAccount = (enableAccReq: EnableAccountReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EnableAccountReq();
      req.setUserId(enableAccReq.userId);
      req.setAccountId(enableAccReq.accountId);
      req.setEnable(this.fromBoolValue(enableAccReq.enable));
      req.setPriority(enableAccReq.priority);

      const client = this.getClient();
      client.enableAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  openTradeWarning = (enableAccReq: OpenTradeWarningReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new OpenTradeWarningReq();
      req.setUserId(enableAccReq.userId);
      req.setAccountId(enableAccReq.accountId);
      req.setIsConfig(enableAccReq.isConfig);
      req.setSlaveIdsList(enableAccReq.slaveIdsList);

      const client = this.getClient();
      client.openTradeWarning(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  activateBetaConnection = (activateBetaReq: ActivateBetaConnectionReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ActivateBetaConnectionReq();
      req.setUserId(activateBetaReq.userId);
      req.setAccountId(activateBetaReq.accountId);
      req.setActivateBeta(activateBetaReq.activateBeta);

      const client = this.getClient();
      client.activateBetaConnection(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  activateAnalyzer = (activateReq: ActivateAnalyzerReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ActivateAnalyzerReq();
      req.setUserId(activateReq.userId);
      req.setAccountId(activateReq.accountId);
      if (activateReq.activate) {
        const boolValue = new BoolValue();
        boolValue.setValue(activateReq.activate.value);
        req.setActivate(boolValue);
      }

      const client = this.getClient();
      client.activateAnalyzer(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listAccountSymbols = (symbolsReq: ListAccountSymbolsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListAccountSymbolsReq();
      req.setUserId(symbolsReq.userId);
      req.setAccountId(symbolsReq.accountId);
      req.setQuery(symbolsReq.query);
      req.setPageSize(symbolsReq.pageSize);
      req.setPaginationCursor(symbolsReq.paginationCursor);

      const client = this.getClient();
      client.listAccountSymbols(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  enableAccountSymbol = (enableReq: EnableAccountSymbolReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EnableAccountSymbolReq();
      req.setUserId(enableReq.userId);
      req.setAccountId(enableReq.accountId);
      req.setSymbolId(enableReq.symbolId);
      req.setEnable(enableReq.enable);

      const client = this.getClient();
      client.enableAccountSymbol(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  enableAllAccountSymbols = (enableReq: EnableAllAccountSymbolsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EnableAllAccountSymbolsReq();
      req.setUserId(enableReq.userId);
      req.setAccountId(enableReq.accountId);
      req.setEnable(enableReq.enable);

      const client = this.getClient();
      client.enableAllAccountSymbols(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listCopiedTrades = (copiedTrades: ListCopiedTradesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListCopiedTradesReq();
      req.setUserId(copiedTrades.userId);
      req.setPageSize(copiedTrades.pageSize);
      req.setPaginationCursor(copiedTrades.paginationCursor);
      req.setSearchTicket(this.fromStingValueWithNull(copiedTrades.searchTicket as any));
      req.setCreatedFrom(toTimestamp(copiedTrades.createdFrom));
      req.setCreatedTo(toTimestamp(copiedTrades.createdTo));

      const client = this.getClient();
      client.listCopiedTrades(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getCopiedTradeLogs = (details: CopiedTradeLogsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new CopiedTradeLogsReq();
      req.setUserId(details.userId);
      req.setCopiedTradeId(details.copiedTradeId);
      req.setMasterAccountId(details.masterAccountId);
      req.setMasterTicket(details.masterTicket);
      req.setAccountId(details.accountId);
      req.setPaginationCursor(details.paginationCursor);
      req.setPageSize(details.pageSize);

      this.getClient().getCopiedTradeLogs(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  compareSymbol = (symbolReq: CompareSymbolsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CompareSymbolsReq();
      req.setUserId(symbolReq.userId);
      req.setMasterAccount(symbolReq.masterAccount);
      req.setSlaveAccount(symbolReq.slaveAccount);
      // req.setShowEmpty(symbolReq.showEmpty);

      const client = this.getClient();
      client.compareSymbols(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getEPSettings = (epReq: GetEPSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetEPSettingsReq();
      req.setUserId(epReq.userId);
      req.setAccountId(epReq.accountId);

      const client = this.getClient();
      client.getEPSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  upsertEPSettings = (epReq: any) => {
    return new Promise((resolve, reject) => {
      let req = new UpsertEPSettingsReq();
      req.setUserId(epReq.userId);
      req.setAccountId(epReq.accountId);

      if (epReq.configurationStyle !== null && epReq.configurationStyle !== undefined) {
        req.setConfigurationStyle(this.fromStingValue(epReq.configurationStyle));
      }

      if (epReq.timelyResetSetting !== null && epReq.timelyResetSetting !== undefined) {
        req.setTimelyResetSetting(this.fromTimelyResetSetting(epReq.timelyResetSetting));
      }

      if (epReq.enableEp !== null && epReq.enableEp !== undefined) {
        req.setEnableEp(this.fromBoolValue(epReq.enableEp));
      }

      if (epReq.manageExisting !== null && epReq.manageExisting !== undefined) {
        req.setManageExisting(this.fromBoolValue(epReq.manageExisting));
      }

      if (epReq.closeAllOpenTrades !== null && epReq.closeAllOpenTrades !== undefined) {
        req.setCloseAllOpenTrades(this.fromBoolValue(epReq.closeAllOpenTrades));
      }

      if (epReq.closeCopiedOpenTrades !== null && epReq.closeCopiedOpenTrades !== undefined) {
        req.setCloseCopiedOpenTrades(this.fromBoolValue(epReq.closeCopiedOpenTrades));
      }

      // if (epReq.disableCopier !== null && epReq.disableCopier !== undefined) {
      //   req.setDisableCopier(this.fromBoolValue(epReq.disableCopier));
      // }

      if (epReq.absoluteMaximum !== null && epReq.absoluteMaximum !== undefined) {
        req.setAbsoluteMaximum(this.fromDoubleValue(epReq.absoluteMaximum));
      }

      if (epReq.absoluteMinimum !== null && epReq.absoluteMinimum !== undefined) {
        req.setAbsoluteMinimum(this.fromDoubleValue(epReq.absoluteMinimum));
      }

      if (epReq.drawDownDifferenceValue !== null && epReq.drawDownDifferenceValue !== undefined) {
        req.setDrawDownDifferenceValue(this.fromDoubleValue(epReq.drawDownDifferenceValue));
      }

      if (epReq.drawDownPercentage !== null && epReq.drawDownPercentage !== undefined) {
        req.setDrawDownPercentage(this.fromDoubleValue(epReq.drawDownPercentage));
      }

      if (epReq.profitDifferenceValue !== null && epReq.profitDifferenceValue !== undefined) {
        req.setProfitDifferenceValue(this.fromDoubleValue(epReq.profitDifferenceValue));
      }

      if (epReq.profitPercentage !== null && epReq.profitPercentage !== undefined) {
        req.setProfitPercentage(this.fromDoubleValue(epReq.profitPercentage));
      }

      const client = this.getClient();
      client.upsertEPSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getMcpSettings = (aiReq: GetMcpSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetMcpSettingsReq();
      req.setUserId(aiReq.userId);
      req.setAccountId(aiReq.accountId);

      const client = this.getClient();
      client.getMcpSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  upsertMcpSettings = (aiReq: UpsertMcpSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpsertMcpSettingsReq();
      req.setUserId(aiReq.userId);
      req.setAccountId(aiReq.accountId);
      req.setTradingEnabled(aiReq.tradingEnabled);
      req.setMaxLotPerTrade(aiReq.maxLotPerTrade);
      req.setAllowedSymbolsList(aiReq.allowedSymbolsList || []);
      req.setBlockedSymbolsList(aiReq.blockedSymbolsList || []);

      const client = this.getClient();
      client.upsertMcpSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getMcpAuditLogs = (aiReq: GetMcpAuditLogsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetMcpAuditLogsReq();
      req.setUserId(aiReq.userId);
      req.setAccountId(aiReq.accountId);
      req.setStatus(aiReq.status || '');
      req.setLimit(aiReq.limit || 0);
      req.setPaginationCursor(aiReq.paginationCursor || '');

      const client = this.getClient();
      client.getMcpAuditLogs(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listIndicatorScripts = (aiReq: ListIndicatorScriptsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListIndicatorScriptsReq();
      req.setUserId(aiReq.userId);

      const client = this.getClient();
      client.listIndicatorScripts(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getIndicatorScript = (aiReq: GetIndicatorScriptReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetIndicatorScriptReq();
      req.setUserId(aiReq.userId);
      req.setScriptId(aiReq.scriptId);

      const client = this.getClient();
      client.getIndicatorScript(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  createIndicatorScript = (aiReq: CreateIndicatorScriptReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CreateIndicatorScriptReq();
      req.setUserId(aiReq.userId);
      req.setName(aiReq.name);
      req.setContent(aiReq.content);
      req.setLanguage(aiReq.language);

      const client = this.getClient();
      client.createIndicatorScript(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateIndicatorScript = (aiReq: UpdateIndicatorScriptReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateIndicatorScriptReq();
      req.setUserId(aiReq.userId);
      req.setScriptId(aiReq.scriptId);
      req.setName(aiReq.name || '');
      req.setContent(aiReq.content || '');

      const client = this.getClient();
      client.updateIndicatorScript(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteIndicatorScript = (aiReq: DeleteIndicatorScriptReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteIndicatorScriptReq();
      req.setUserId(aiReq.userId);
      req.setScriptId(aiReq.scriptId);

      const client = this.getClient();
      client.deleteIndicatorScript(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  closeAccountsTrades = (epReq: CloseAccountsTradesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CloseAccountsTradesReq();
      req.setUserId(epReq.userId);
      req.setAccountId(epReq.accountId);

      const client = this.getClient();
      client.closeAccountsTrades(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  symbolStats = (symReq: SymbolStatsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SymbolStatsReq();
      req.setUserId(symReq.userId);
      req.setMasterAccount(symReq.masterAccount);
      req.setSlaveAccount(symReq.slaveAccount);
      req.setSlaveId(symReq.slaveId);

      const client = this.getClient();
      client.symbolStats(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  symbolLotOverride = (symReq: SymbolLotOverrideReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SymbolLotOverrideReq();
      req.setUserId(symReq.userId);
      req.setAccountId(symReq.accountId);
      req.setSymbolIdsList(symReq.symbolIdsList);
      req.setMinLots(this.fromDoubleValue(symReq.minLots));
      req.setMaxLots(this.fromDoubleValue(symReq.maxLots));

      const client = this.getClient();
      client.symbolLotOverride(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteSymbolLotOverride = (symReq: DeleteSymbolLotOverrideReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteSymbolLotOverrideReq();
      req.setUserId(symReq.userId);
      req.setAccountId(symReq.accountId);
      req.setSymbolIdsList(symReq.symbolIdsList);

      const client = this.getClient();
      client.deleteSymbolLotOverride(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  unmappedSymbolList = (symbolsReq: UnmappedSymbolListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UnmappedSymbolListReq();
      req.setUserId(symbolsReq.userId);
      req.setPageSize(symbolsReq.pageSize);
      req.setPaginationCursor(symbolsReq.paginationCursor);
      req.setMasterAccount(symbolsReq.masterAccount);
      req.setSlaveId(symbolsReq.slaveId);
      req.setGroup(symbolsReq.group);
      req.setQuery(symbolsReq.query);

      const client = this.getClient();
      client.unmappedSymbolList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  mappedSymbolList = (symbolsReq: MappedSymbolListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new MappedSymbolListReq();
      req.setUserId(symbolsReq.userId);
      req.setPageSize(symbolsReq.pageSize);
      req.setPaginationCursor(symbolsReq.paginationCursor);
      req.setMasterAccount(symbolsReq.masterAccount);
      req.setSlaveId(symbolsReq.slaveId);
      req.setGroup(symbolsReq.group);
      req.setQuery(symbolsReq.query);

      const client = this.getClient();
      client.mappedSymbolList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  autoMapSymbols = (mapReq: AutoMapSymbolsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AutoMapSymbolsReq();
      req.setUserId(mapReq.userId);
      req.setMasterAccount(mapReq.masterAccount);
      req.setSlaveAccount(mapReq.slaveAccount);
      req.setSlaveId(mapReq.slaveId);
      req.setPattern(mapReq.pattern);
      req.setOverwriteExisting(mapReq.overwriteExisting);

      const client = this.getClient();
      client.autoMapSymbols(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  groupList = (mapReq: GroupListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GroupListReq();
      req.setUserId(mapReq.userId);
      req.setAccountsList(mapReq.accountsList);

      const client = this.getClient();
      client.groupList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getUsersDedicatedEnvList = (dediReq: { userId: string; envType?: GetUsersDedicatedEnvListReq.AsObject['envType'] }) => {
    return new Promise((resolve, reject) => {
      let req = new GetUsersDedicatedEnvListReq();
      req.setUserId(dediReq.userId);
      if (dediReq.envType != null) req.setEnvType(dediReq.envType);

      const client = this.getClient();
      client.getUsersDedicatedEnvList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  changeAccountEnv = (dediReq: ChangeAccountEnvReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ChangeAccountEnvReq();
      req.setUserId(dediReq.userId);
      req.setDedicatedEnvId(this.fromStingValueWithNull(dediReq.dedicatedEnvId));
      req.setAccountId(dediReq.accountId);

      const client = this.getClient();
      client.changeAccountEnv(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getUsersRithmicProfiles = (req: GetUsersRithmicProfilesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let request = new GetUsersRithmicProfilesReq();
      request.setUserId(req.userId);

      const client = this.getClient();
      client.getUsersRithmicProfiles(request, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  changeProfileEnv = (req: ChangeProfileEnvReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let request = new ChangeProfileEnvReq();
      request.setUserId(req.userId);
      request.setCredentialId(req.credentialId);
      request.setDedicatedEnvId(this.fromStingValueWithNull(req.dedicatedEnvId));

      const client = this.getClient();
      client.changeProfileEnv(request, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateDedicatedEnv = (dediReq: UpdateDedicatedEnvReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateDedicatedEnvReq();
      req.setUserId(dediReq.userId);
      req.setDedicatedEnvId(dediReq.dedicatedEnvId);
      req.setNickName(dediReq.nickName);

      const client = this.getClient();
      client.updateDedicatedEnv(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  checkAvailableEnv = (dediReq: CheckAvailableEnvReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CheckAvailableEnvReq();
      req.setUserId(dediReq.userId);

      const client = this.getClient();
      client.checkAvailableEnv(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getAllowedCTraderAccounts = (allowedReq: GetAllowedCTraderAccountsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetAllowedCTraderAccountsReq();
      req.setUserId(allowedReq.userId);
      req.setAuthId(allowedReq.authId);

      const client = this.getClient();
      client.getAllowedCTraderAccounts(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getTradovateUserAccountList = (allowedReq: GetTradovateUserAccountListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetTradovateUserAccountListReq();
      req.setUserId(allowedReq.userId);
      req.setAuthId(allowedReq.authId);

      const client = this.getClient();
      client.getTradovateUserAccountList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getCTraderAuthenticatedProfileList = (allowedReq: GetCTraderAuthenticatedProfileListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetCTraderAuthenticatedProfileListReq();
      req.setUserId(allowedReq.userId);

      const client = this.getClient();
      client.getCTraderAuthenticatedProfileList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getTradovateProfileList = (allowedReq: GetTradovateProfileListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetTradovateProfileListReq();
      req.setUserId(allowedReq.userId);

      const client = this.getClient();
      client.getTradovateProfileList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateCTraderAuthInfo = (allowedReq: UpdateCTraderAuthInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateCTraderAuthInfoReq();
      req.setUserId(allowedReq.userId);
      req.setAuthId(allowedReq.authId);
      req.setNickname(this.fromStingValue(allowedReq.nickname));

      const client = this.getClient();
      client.updateCTraderAuthInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteCTraderAuthInfo = (allowedReq: DeleteCTraderAuthInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteCTraderAuthInfoReq();
      req.setUserId(allowedReq.userId);
      req.setAuthId(allowedReq.authId);

      const client = this.getClient();
      client.deleteCTraderAuthInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateTradovateAuthInfo = (allowedReq: UpdateTradovateAuthInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateTradovateAuthInfoReq();
      req.setUserId(allowedReq.userId);
      req.setAuthId(allowedReq.authId);
      req.setNickname(this.fromStingValue(allowedReq.nickname));

      const client = this.getClient();
      client.updateTradovateAuthInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteTradovateAuthInfo = (allowedReq: DeleteTradovateAuthInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteTradovateAuthInfoReq();
      req.setUserId(allowedReq.userId);
      req.setAuthId(allowedReq.authId);

      const client = this.getClient();
      client.deleteTradovateAuthInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  saveCTraderAuthInfo = (allowedReq: SaveCTraderAuthInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SaveCTraderAuthInfoReq();
      req.setUserId(allowedReq.userId);
      req.setAuthorizationCode(allowedReq.authorizationCode);
      req.setRedirectUrl(allowedReq.redirectUrl);

      const client = this.getClient();
      client.saveCTraderAuthInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  saveTVAuthInfo = (allowedReq: SaveTradovateAuthInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SaveTradovateAuthInfoReq();
      req.setUserId(allowedReq.userId);
      req.setAuthorizationCode(allowedReq.authorizationCode);
      req.setRedirectUrl(allowedReq.redirectUrl);
      req.setIsDemo(allowedReq.isDemo);

      const client = this.getClient();
      client.saveTradovateAuthInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  fromTimelyResetSetting = (s: TimelyReset.AsObject | undefined): TimelyReset => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let timelyReset = new TimelyReset();
    if (s.drawdownResetPercent !== null && s.drawdownResetPercent !== undefined) {
      timelyReset.setDrawdownResetPercent(this.fromDoubleValue(s.drawdownResetPercent));
    }
    if (s.profitResetPercent !== null && s.profitResetPercent !== undefined) {
      timelyReset.setProfitResetPercent(this.fromDoubleValue(s.profitResetPercent));
    }
    if (s.resetClockTime !== null && s.resetClockTime !== undefined) {
      timelyReset.setResetClockTime(this.fromInt64Value(s.resetClockTime));
    }
    return timelyReset;
  };

  fromInt64Value = (s: Int64Value.AsObject | undefined): Int64Value => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let intVal = new Int64Value();
    intVal.setValue(s.value);
    return intVal;
  };

  fromStingValue = (s: StringValue.AsObject | undefined): StringValue => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let stringValue = new StringValue();
    stringValue.setValue(s.value);
    return stringValue;
  };

  fromStingValueWithNull = (s: StringValue.AsObject | undefined): StringValue => {
    if (s === null || s === undefined || s.value === '') {
      return null as any;
    }
    let stringValue = new StringValue();
    stringValue.setValue(s.value);
    return stringValue;
  };

  fromBoolValue = (s: BoolValue.AsObject | undefined): BoolValue => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let boolValue = new BoolValue();
    boolValue.setValue(s.value);
    return boolValue;
  };

  fromCTraderReqInfo = (s: CTraderInfoReq.AsObject | undefined): CTraderInfoReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new CTraderInfoReq();
    info.setAuthId(s.authId);
    return info;
  };

  fromTradeLockerReqInfo = (s: TradeLockerInfoReq.AsObject | undefined): TradeLockerInfoReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new TradeLockerInfoReq();
    info.setEmail(s.email);
    info.setIsLive(s.isLive);
    info.setBetaUser(s.betaUser);
    return info;
  };

  fromMTRReqInfo = (s: MatchTreaderInfoReq.AsObject | undefined): MatchTreaderInfoReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new MatchTreaderInfoReq();
    info.setEmail(s.email);
    info.setBrokerId(s.brokerId);
    return info;
  };

  fromTVReqInfo = (s: TradovateInfoReq.AsObject | undefined): TradovateInfoReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new TradovateInfoReq();
    info.setAuthId(s.authId);
    info.setIsDemo(s.isDemo);
    return info;
  };

  fromProjectXReqInfo = (s: ProjectXReq.AsObject | undefined): ProjectXReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new ProjectXReq();
    info.setUsername(s.username);
    return info;
  };

  fromRithmicReqInfo = (s: RithmicReq.AsObject | undefined): RithmicReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new RithmicReq();
    info.setUser(s.user);
    info.setSystemName(s.systemName);
    return info;
  };

  fromDxtInfo = (s: DxtInfoReq.AsObject | undefined): DxtInfoReq => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let info = new DxtInfoReq();
    info.setUsername(s.username);
    return info;
  };

  fromDoubleValue = (s: DoubleValue.AsObject | undefined): DoubleValue => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let doubleValue = new DoubleValue();
    doubleValue.setValue(s.value);
    return doubleValue;
  };
}

export class TradeClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__tradeManagerClient != null) {
      return __tradeManagerClient;
    }
    __tradeManagerClient = new TradeCopierClient(gatewayAddress);
    return __tradeManagerClient;
  };

  getUserConfigs = (userConfigReq: GetUserConfigsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetUserConfigsReq();
      req.setUserId(userConfigReq.userId);

      const client = this.getClient();
      client.getUserConfigs(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  addConfig = (addConfigReq: AddConfigReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddConfigReq();
      req.setMasterAccount(addConfigReq.masterAccount);
      req.setUserId(addConfigReq.userId);

      const client = this.getClient();
      client.addConfig(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  addSlave = (addSlaveReq: AddSlavesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddSlavesReq();
      req.setUserId(addSlaveReq.userId);
      req.setConfigId(addSlaveReq.configId);
      req.setSlaveAccountsList(addSlaveReq.slaveAccountsList);

      const client = this.getClient();
      client.addSlaves(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateSlave = (updateSlaveReq: UpdateSlaveReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateSlaveReq();
      req.setConfigId(updateSlaveReq.configId);
      req.setUserId(updateSlaveReq.userId);
      req.setBalanceMultiplierValue(this.fromInt32Value(updateSlaveReq.balanceMultiplierValue as any));
      req.setEnable(this.fromBoolValue(updateSlaveReq.enable as any));
      req.setCopyPendingOrders(this.fromBoolValue(updateSlaveReq.copyPendingOrders as any));
      req.setCopyStopLoss(this.fromBoolValue(updateSlaveReq.copyStopLoss as any));
      req.setCopyTakeProfit(this.fromBoolValue(updateSlaveReq.copyTakeProfit as any));
      req.setReverseTrade(this.fromBoolValue(updateSlaveReq.reverseTrade as any));
      req.setFixedLotValue(this.fromInt32Value(updateSlaveReq.fixedLotValue as any));
      req.setLotMultiplierValue(this.fromInt32Value(updateSlaveReq.lotMultiplierValue as any));
      req.setRiskType(updateSlaveReq.riskType);
      req.setSlaveId(updateSlaveReq.slaveId);
      req.setForceMinimumLotSize(this.fromBoolValue(updateSlaveReq.forceMinimumLotSize as any));
      req.setForceMaximumLotSize(this.fromBoolValue(updateSlaveReq.forceMaximumLotSize as any));
      req.setCopyComment(this.fromBoolValue(updateSlaveReq.copyComment as any));
      req.setCustomComment(this.fromStringValue(updateSlaveReq.customComment as any));
      req.setStrictClose(this.fromBoolValue(updateSlaveReq.strictClose as any));
      req.setConsiderContractSize(this.fromBoolValue(updateSlaveReq.considerContractSize as any));
      req.setCopyMagicNumber(this.fromBoolValue(updateSlaveReq.copyMagicNumber as any));
      req.setMagicNumber(this.fromInt64Value(updateSlaveReq.magicNumber as any));
      req.setManageExisting(this.fromBoolValue(updateSlaveReq.manageExisting as any));
      req.setManageExisting(this.fromBoolValue(updateSlaveReq.manageExisting as any));
      req.setFixedBalanceMultiplierValue(this.fromInt32Value(updateSlaveReq.fixedBalanceMultiplierValue as any));
      req.setFixedMasterBalance(this.fromInt32Value(updateSlaveReq.fixedMasterBalance as any));
      req.setLotRefinerValue(this.fromInt32Value(updateSlaveReq.lotRefinerValue as any));
      req.setMinDelayTime(this.fromInt64Value(updateSlaveReq.minDelayTime as any));
      req.setMaxDelayTime(this.fromInt64Value(updateSlaveReq.maxDelayTime as any));
      req.setMagicFiltersList(this.fromInt64ValueArr(updateSlaveReq.magicFiltersList));
      req.setCopyExpiryTime(this.fromBoolValue(updateSlaveReq.copyExpiryTime as any));
      req.setCommentFiltersList(this.fromStringValueArr(updateSlaveReq.commentFiltersList));
      req.setMinLotFilter(this.fromInt64Value(updateSlaveReq.minLotFilter as any));
      req.setMaxLotFilter(this.fromInt64Value(updateSlaveReq.maxLotFilter as any));
      req.setStopLossPip(this.fromDoubleValue(updateSlaveReq.stopLossPip as any));
      req.setTakeProfitPip(this.fromDoubleValue(updateSlaveReq.takeProfitPip as any));
      req.setSlRefiner(this.fromDoubleValue(updateSlaveReq.slRefiner as any));
      req.setTpRefiner(this.fromDoubleValue(updateSlaveReq.tpRefiner as any));
      req.setBalancePercentageValue(this.fromDoubleValue(updateSlaveReq.balancePercentageValue as any));
      req.setCopyExistingTrades(this.fromBoolValue(updateSlaveReq.copyExistingTrades as any));
      req.setAllowedTradeSide(this.fromStringValueNull(updateSlaveReq.allowedTradeSide));
      req.setTradingHoursList(this.fromTradingHourList(updateSlaveReq.tradingHoursList));
      req.setMagicFilterType(updateSlaveReq.magicFilterType);
      req.setCommentFilterType(updateSlaveReq.commentFilterType);
      req.setCustomPlacedType(this.fromStringValue({ value: 'byexpert' }));

      const client = this.getClient();
      client.updateSlave(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  overrideSymbolSettings = (updateSlaveReq: UpdateSymbolSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateSymbolSettingsReq();
      req.setUserId(updateSlaveReq.userId);
      req.setConfigId(updateSlaveReq.configId);
      req.setMasterSymbolName(updateSlaveReq.masterSymbolName);
      req.setSlaveSymbolName(updateSlaveReq.slaveSymbolName);
      req.setBalanceMultiplierValue(this.fromInt32Value(updateSlaveReq.balanceMultiplierValue as any));
      req.setEnable(this.fromBoolValue(updateSlaveReq.enable as any));
      req.setCopyPendingOrders(this.fromBoolValue(updateSlaveReq.copyPendingOrders as any));
      req.setCopyStopLoss(this.fromBoolValue(updateSlaveReq.copyStopLoss as any));
      req.setCopyTakeProfit(this.fromBoolValue(updateSlaveReq.copyTakeProfit as any));
      req.setReverseTrade(this.fromBoolValue(updateSlaveReq.reverseTrade as any));
      req.setFixedLotValue(this.fromInt32Value(updateSlaveReq.fixedLotValue as any));
      req.setLotMultiplierValue(this.fromInt32Value(updateSlaveReq.lotMultiplierValue as any));
      req.setRiskType(updateSlaveReq.riskType);
      req.setSlaveId(updateSlaveReq.slaveId);
      req.setForceMinimumLotSize(this.fromBoolValue(updateSlaveReq.forceMinimumLotSize as any));
      req.setForceMaximumLotSize(this.fromBoolValue(updateSlaveReq.forceMaximumLotSize as any));
      req.setCopyComment(this.fromBoolValue(updateSlaveReq.copyComment as any));
      req.setCustomComment(this.fromStringValue(updateSlaveReq.customComment as any));
      req.setStrictClose(this.fromBoolValue(updateSlaveReq.strictClose as any));
      req.setConsiderContractSize(this.fromBoolValue(updateSlaveReq.considerContractSize as any));
      req.setCopyMagicNumber(this.fromBoolValue(updateSlaveReq.copyMagicNumber as any));
      req.setMagicNumber(this.fromInt64Value(updateSlaveReq.magicNumber as any));
      req.setManageExisting(this.fromBoolValue(updateSlaveReq.manageExisting as any));
      req.setManageExisting(this.fromBoolValue(updateSlaveReq.manageExisting as any));
      req.setFixedBalanceMultiplierValue(this.fromInt32Value(updateSlaveReq.fixedBalanceMultiplierValue as any));
      req.setFixedMasterBalance(this.fromInt32Value(updateSlaveReq.fixedMasterBalance as any));
      req.setLotRefinerValue(this.fromInt32Value(updateSlaveReq.lotRefinerValue as any));
      req.setMinDelayTime(this.fromInt64Value(updateSlaveReq.minDelayTime as any));
      req.setMaxDelayTime(this.fromInt64Value(updateSlaveReq.maxDelayTime as any));
      req.setMagicFiltersList(this.fromInt64ValueArr(updateSlaveReq.magicFiltersList));
      req.setCopyExpiryTime(this.fromBoolValue(updateSlaveReq.copyExpiryTime as any));
      req.setCommentFiltersList(this.fromStringValueArr(updateSlaveReq.commentFiltersList));
      req.setMinLotFilter(this.fromInt64Value(updateSlaveReq.minLotFilter as any));
      req.setMaxLotFilter(this.fromInt64Value(updateSlaveReq.maxLotFilter as any));
      req.setStopLossPip(this.fromDoubleValue(updateSlaveReq.stopLossPip as any));
      req.setTakeProfitPip(this.fromDoubleValue(updateSlaveReq.takeProfitPip as any));
      req.setSlRefiner(this.fromDoubleValue(updateSlaveReq.slRefiner as any));
      req.setTpRefiner(this.fromDoubleValue(updateSlaveReq.tpRefiner as any));
      req.setBalancePercentageValue(this.fromDoubleValue(updateSlaveReq.balancePercentageValue as any));
      // req.setCopyExistingTrades(this.fromBoolValue(updateSlaveReq.copyExistingTrades as any));
      req.setAllowedTradeSide(this.fromStringValueNull(updateSlaveReq.allowedTradeSide));
      // req.setTradingHoursList(this.fromTradingHourList(updateSlaveReq.tradingHoursList));
      req.setMagicFilterType(updateSlaveReq.magicFilterType);
      req.setCommentFilterType(updateSlaveReq.commentFilterType);
      req.setCustomPlacedType(this.fromStringValue({ value: 'byexpert' }));

      const client = this.getClient();
      client.updateSymbolSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  associatedMasterSymbol = (symbolReq: AssociatedMasterSymbolReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AssociatedMasterSymbolReq();
      req.setUserId(symbolReq.userId);
      req.setSlaveId(symbolReq.slaveId);
      req.setSlaveSymbol(symbolReq.slaveSymbol);

      const client = this.getClient();
      client.associatedMasterSymbol(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteSymbolSettings = (symbolReq: DeleteSymbolSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteSymbolSettingsReq();
      req.setUserId(symbolReq.userId);
      req.setSlaveId(symbolReq.slaveId);
      req.setMappingId(symbolReq.mappingId);

      const client = this.getClient();
      client.deleteSymbolSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  copyExistingTrade = (updateSlaveReq: CopyExistingTradeReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CopyExistingTradeReq();
      req.setUserId(updateSlaveReq.userId);
      req.setMasterAccountId(updateSlaveReq.masterAccountId);
      req.setSlaveId(updateSlaveReq.slaveId);

      const client = this.getClient();
      client.copyExistingTrade(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  simulateTrade = (simulateReq: SimulateTradeReq.AsObject): Promise<SimulateTradeReply.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new SimulateTradeReq();
      req.setUserId(simulateReq.userId);
      req.setMasterAccount(simulateReq.masterAccount);
      req.setSlaveAccount(simulateReq.slaveAccount);

      if (simulateReq.slaveConfig) {
        req.setSlaveConfig(this.fromUpdatedSlaveConfig(simulateReq.slaveConfig));
      }

      if (simulateReq.masterOrder) {
        const order = new MasterOrderInfo();
        order.setOrderType(simulateReq.masterOrder.orderType);
        order.setSymbol(simulateReq.masterOrder.symbol);
        order.setLots(simulateReq.masterOrder.lots);
        order.setOpenPrice(simulateReq.masterOrder.openPrice);
        order.setTakeProfit(simulateReq.masterOrder.takeProfit);
        order.setStopLoss(simulateReq.masterOrder.stopLoss);
        order.setMagicNumber(simulateReq.masterOrder.magicNumber);
        order.setPlacedType(simulateReq.masterOrder.placedType);
        order.setExpiration(simulateReq.masterOrder.expiration);
        order.setComment(simulateReq.masterOrder.comment);
        order.setIsPendingFill(simulateReq.masterOrder.isPendingFill);
        req.setMasterOrder(order);
      }

      const slaveSymbol = simulateReq.slaveSymbol?.value?.trim();
      if (slaveSymbol) {
        req.setSlaveSymbol(this.fromStringValue({ value: slaveSymbol }));
      }

      const client = this.getClient();
      client.simulateTrade(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  updateSlaveMany = (updateSlaveReq: UpdateSlaveManyReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateSlaveManyReq();
      req.setConfigId(updateSlaveReq.configId);
      req.setUserId(updateSlaveReq.userId);
      req.setUpdatedSlaveConfigsList(this.fromUpdatedSlaveConfigsList(updateSlaveReq.updatedSlaveConfigsList));

      const client = this.getClient();
      client.updateSlaveMany(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  fromUpdatedSlaveConfig = (updateSlaveReq: UpdatedSlaveConfig.AsObject): UpdatedSlaveConfig => {
    const req = new UpdatedSlaveConfig();
    req.setId(updateSlaveReq.id);
    req.setBalanceMultiplierValue(this.fromInt32Value(updateSlaveReq.balanceMultiplierValue as any));
    req.setEnable(this.fromBoolValue(updateSlaveReq.enable as any));
    req.setCopyPendingOrders(this.fromBoolValue(updateSlaveReq.copyPendingOrders as any));
    req.setCopyStopLoss(this.fromBoolValue(updateSlaveReq.copyStopLoss as any));
    req.setCopyTakeProfit(this.fromBoolValue(updateSlaveReq.copyTakeProfit as any));
    req.setReverseTrade(this.fromBoolValue(updateSlaveReq.reverseTrade as any));
    req.setFixedLotValue(this.fromInt32Value(updateSlaveReq.fixedLotValue as any));
    req.setLotMultiplierValue(this.fromInt32Value(updateSlaveReq.lotMultiplierValue as any));
    req.setRiskType(updateSlaveReq.riskType);
    req.setForceMinimumLotSize(this.fromBoolValue(updateSlaveReq.forceMinimumLotSize as any));
    req.setForceMaximumLotSize(this.fromBoolValue(updateSlaveReq.forceMaximumLotSize as any));
    req.setCopyComment(this.fromBoolValue(updateSlaveReq.copyComment as any));
    req.setCustomComment(this.fromStringValue(updateSlaveReq.customComment as any));
    req.setStrictClose(this.fromBoolValue(updateSlaveReq.strictClose as any));
    req.setConsiderContractSize(this.fromBoolValue(updateSlaveReq.considerContractSize as any));
    req.setCopyMagicNumber(this.fromBoolValue(updateSlaveReq.copyMagicNumber as any));
    req.setMagicNumber(this.fromInt64Value(updateSlaveReq.magicNumber as any));
    req.setManageExisting(this.fromBoolValue(updateSlaveReq.manageExisting as any));
    req.setFixedBalanceMultiplierValue(this.fromInt32Value(updateSlaveReq.fixedBalanceMultiplierValue as any));
    req.setFixedMasterBalance(this.fromInt32Value(updateSlaveReq.fixedMasterBalance as any));
    req.setLotRefinerValue(this.fromInt32Value(updateSlaveReq.lotRefinerValue as any));
    req.setMinDelayTime(this.fromInt64Value(updateSlaveReq.minDelayTime as any));
    req.setMaxDelayTime(this.fromInt64Value(updateSlaveReq.maxDelayTime as any));
    req.setMagicFiltersList(this.fromInt64ValueArr(updateSlaveReq.magicFiltersList));
    req.setCopyExpiryTime(this.fromBoolValue(updateSlaveReq.copyExpiryTime as any));
    req.setCommentFiltersList(this.fromStringValueArr(updateSlaveReq.commentFiltersList));
    req.setMinLotFilter(this.fromInt64Value(updateSlaveReq.minLotFilter as any));
    req.setMaxLotFilter(this.fromInt64Value(updateSlaveReq.maxLotFilter as any));
    req.setStopLossPip(this.fromDoubleValue(updateSlaveReq.stopLossPip as any));
    req.setTakeProfitPip(this.fromDoubleValue(updateSlaveReq.takeProfitPip as any));
    req.setSlRefiner(this.fromDoubleValue(updateSlaveReq.slRefiner as any));
    req.setTpRefiner(this.fromDoubleValue(updateSlaveReq.tpRefiner as any));
    req.setBalancePercentageValue(this.fromDoubleValue(updateSlaveReq.balancePercentageValue as any));
    req.setAllowedTradeSide(this.fromStringValueNull(updateSlaveReq.allowedTradeSide));
    req.setMagicFilterType(updateSlaveReq.magicFilterType);
    req.setCommentFilterType(updateSlaveReq.commentFilterType);
    req.setCustomPlacedType(this.fromStringValue({ value: 'byexpert' }));
    return req;
  };

  fromUpdatedSlaveConfigsList = (updatedSlaveConfigsList: UpdatedSlaveConfig.AsObject[]) => {
    return updatedSlaveConfigsList.map((c) => this.fromUpdatedSlaveConfig(c));
  };

  fromDoubleValue = (s: DoubleValue.AsObject | undefined): DoubleValue => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let doubleValue = new DoubleValue();
    doubleValue.setValue(s.value);
    return doubleValue;
  };

  deleteConfig = (deleteConfigReq: DeleteConfigReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteConfigReq();
      req.setUserId(deleteConfigReq.userId);
      req.setConfigId(deleteConfigReq.configId);

      const client = this.getClient();
      client.deleteConfig(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateConfig = (updateConfigReq: UpdateConfigReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateConfigReq();
      req.setUserId(updateConfigReq.userId);
      req.setConfigId(updateConfigReq.configId);
      req.setMasterAccountId(updateConfigReq.masterAccountId);
      req.setEnable(this.fromBoolValue(updateConfigReq.enable));
      req.setManageExisting(this.fromBoolValue(updateConfigReq.manageExisting));

      const client = this.getClient();
      client.updateConfig(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteSlave = (deleteSlaveReq: DeleteSalvesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteSalvesReq();
      req.setUserId(deleteSlaveReq.userId);
      req.setConfigId(deleteSlaveReq.configId);
      req.setSlaveAccountsList(deleteSlaveReq.slaveAccountsList);

      const client = this.getClient();
      client.deleteSlaves(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  addSymbolMapping = (addSymbolReq: AddSymbolMappingReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddSymbolMappingReq();
      req.setUserId(addSymbolReq.userId);
      // req.setMaster(addSymbolReq.master);
      req.setSlaveId(addSymbolReq.slaveId);
      req.setMappingsList(this.fromMappingList(addSymbolReq.mappingsList));
      // req.setName(addSymbolReq.name);

      const client = this.getClient();
      client.addSymbolMapping(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listSymbolMappings = (symbolReq: ListSymbolMappingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListSymbolMappingsReq();
      req.setUserId(symbolReq.userId);
      req.setSlaveId(symbolReq.slaveId);

      const client = this.getClient();
      client.listSymbolMappings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteSymbolMapping = (deleteReq: DeleteSymbolMappingReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteSymbolMappingReq();
      req.setUserId(deleteReq.userId);
      req.setSlaveId(deleteReq.slaveId);
      req.setSymbolId(deleteReq.symbolId);
      req.setDeleteAll(deleteReq.deleteAll);

      const client = this.getClient();
      client.deleteSymbolMapping(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  editSymbolMapping = (editReq: EditSymbolMappingReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EditSymbolMappingReq();
      req.setUserId(editReq.userId);
      req.setSlaveId(editReq.slaveId);
      req.setSymbolId(editReq.symbolId);
      req.setName(editReq.name);

      const client = this.getClient();
      client.editSymbolMapping(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listSymbolSettings = (symbolReq: ListSymbolSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListSymbolSettingsReq();
      req.setUserId(symbolReq.userId);
      req.setSlaveId(symbolReq.slaveId);

      const client = this.getClient();
      client.listSymbolSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  pauseAccountSymbol = (pauseReq: PauseAccountSymbolReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new PauseAccountSymbolReq();
      req.setUserId(pauseReq.userId);
      req.setSlaveId(pauseReq.slaveId);
      req.setMasterSymbol(pauseReq.masterSymbol);
      req.setSlaveSymbol(pauseReq.slaveSymbol);
      if (pauseReq.pauseUntil && pauseReq.pauseUntil.seconds > 0) {
        req.setPauseUntil(this.fromTimestamp(pauseReq.pauseUntil));
      }

      const client = this.getClient();
      client.pauseAccountSymbol(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listCopierTemplate = (pauseReq: ListCopierTemplateReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListCopierTemplateReq();
      req.setUserId(pauseReq.userId);

      const client = this.getClient();
      client.listCopierTemplate(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteCopierTemplate = (templateReq: DeleteCopierTemplateReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteCopierTemplateReq();
      req.setUserId(templateReq.userId);
      req.setId(templateReq.id);

      const client = this.getClient();
      client.deleteCopierTemplate(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateCopierTemplate = (addReq: UpdateCopierTemplateReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateCopierTemplateReq();
      req.setUserId(addReq.userId);
      req.setId(addReq.id);
      req.setName(addReq.name);
      req.setBalanceMultiplierValue(this.fromInt32Value(addReq.balanceMultiplierValue as any));
      req.setCopyPendingOrders(this.fromBoolValue(addReq.copyPendingOrders as any));
      req.setCopyStopLoss(this.fromBoolValue(addReq.copyStopLoss as any));
      req.setCopyTakeProfit(this.fromBoolValue(addReq.copyTakeProfit as any));
      req.setReverseTrade(this.fromBoolValue(addReq.reverseTrade as any));
      req.setFixedLotValue(this.fromInt32Value(addReq.fixedLotValue as any));
      req.setLotMultiplierValue(this.fromInt32Value(addReq.lotMultiplierValue as any));
      req.setRiskType(addReq.riskType);
      req.setForceMinimumLotSize(this.fromBoolValue(addReq.forceMinimumLotSize as any));
      req.setForceMaximumLotSize(this.fromBoolValue(addReq.forceMaximumLotSize as any));
      req.setCopyComment(this.fromBoolValue(addReq.copyComment as any));
      req.setCustomComment(this.fromStringValue(addReq.customComment as any));
      req.setCustomPlacedType(this.fromStringValue(addReq.customPlacedType as any));
      req.setStrictClose(this.fromBoolValue(addReq.strictClose as any));
      req.setConsiderContractSize(this.fromBoolValue(addReq.considerContractSize as any));
      req.setCopyMagicNumber(this.fromBoolValue(addReq.copyMagicNumber as any));
      req.setMagicNumber(this.fromInt64Value(addReq.magicNumber as any));
      req.setFixedBalanceMultiplierValue(this.fromInt32Value(addReq.fixedBalanceMultiplierValue as any));
      req.setFixedMasterBalance(this.fromInt32Value(addReq.fixedMasterBalance as any));
      req.setLotRefinerValue(this.fromInt32Value(addReq.lotRefinerValue as any));
      req.setMinDelayTime(this.fromInt64Value(addReq.minDelayTime as any));
      req.setMaxDelayTime(this.fromInt64Value(addReq.maxDelayTime as any));
      req.setMagicFiltersList(this.fromInt64ValueArr(addReq.magicFiltersList));
      req.setCopyExpiryTime(this.fromBoolValue(addReq.copyExpiryTime as any));
      req.setCommentFiltersList(this.fromStringValueArr(addReq.commentFiltersList));
      req.setMinLotFilter(this.fromInt64Value(addReq.minLotFilter as any));
      req.setMaxLotFilter(this.fromInt64Value(addReq.maxLotFilter as any));
      req.setStopLossPip(this.fromDoubleValue(addReq.stopLossPip as any));
      req.setTakeProfitPip(this.fromDoubleValue(addReq.takeProfitPip as any));
      req.setSlRefiner(this.fromDoubleValue(addReq.slRefiner as any));
      req.setTpRefiner(this.fromDoubleValue(addReq.tpRefiner as any));
      req.setBalancePercentageValue(this.fromDoubleValue(addReq.balancePercentageValue as any));
      req.setAllowedTradeSide(this.fromStringValueNull(addReq.allowedTradeSide));
      req.setMagicFilterType(addReq.magicFilterType);
      req.setCommentFilterType(addReq.commentFilterType);

      const client = this.getClient();
      client.updateCopierTemplate(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  addCopierTemplate = (addReq: AddCopierTemplateReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddCopierTemplateReq();
      req.setUserId(addReq.userId);
      req.setName(addReq.name);
      req.setBalanceMultiplierValue(this.fromInt32Value(addReq.balanceMultiplierValue as any));
      req.setCopyPendingOrders(this.fromBoolValue(addReq.copyPendingOrders as any));
      req.setCopyStopLoss(this.fromBoolValue(addReq.copyStopLoss as any));
      req.setCopyTakeProfit(this.fromBoolValue(addReq.copyTakeProfit as any));
      req.setReverseTrade(this.fromBoolValue(addReq.reverseTrade as any));
      req.setFixedLotValue(this.fromInt32Value(addReq.fixedLotValue as any));
      req.setLotMultiplierValue(this.fromInt32Value(addReq.lotMultiplierValue as any));
      req.setRiskType(addReq.riskType);
      req.setForceMinimumLotSize(this.fromBoolValue(addReq.forceMinimumLotSize as any));
      req.setForceMaximumLotSize(this.fromBoolValue(addReq.forceMaximumLotSize as any));
      req.setCopyComment(this.fromBoolValue(addReq.copyComment as any));
      req.setCustomComment(this.fromStringValue(addReq.customComment as any));
      req.setCustomPlacedType(this.fromStringValue(addReq.customPlacedType as any));
      req.setStrictClose(this.fromBoolValue(addReq.strictClose as any));
      req.setConsiderContractSize(this.fromBoolValue(addReq.considerContractSize as any));
      req.setCopyMagicNumber(this.fromBoolValue(addReq.copyMagicNumber as any));
      req.setMagicNumber(this.fromInt64Value(addReq.magicNumber as any));
      req.setFixedBalanceMultiplierValue(this.fromInt32Value(addReq.fixedBalanceMultiplierValue as any));
      req.setFixedMasterBalance(this.fromInt32Value(addReq.fixedMasterBalance as any));
      req.setLotRefinerValue(this.fromInt32Value(addReq.lotRefinerValue as any));
      req.setMinDelayTime(this.fromInt64Value(addReq.minDelayTime as any));
      req.setMaxDelayTime(this.fromInt64Value(addReq.maxDelayTime as any));
      req.setMagicFiltersList(this.fromInt64ValueArr(addReq.magicFiltersList));
      req.setCopyExpiryTime(this.fromBoolValue(addReq.copyExpiryTime as any));
      req.setCommentFiltersList(this.fromStringValueArr(addReq.commentFiltersList));
      req.setMinLotFilter(this.fromInt64Value(addReq.minLotFilter as any));
      req.setMaxLotFilter(this.fromInt64Value(addReq.maxLotFilter as any));
      req.setStopLossPip(this.fromDoubleValue(addReq.stopLossPip as any));
      req.setTakeProfitPip(this.fromDoubleValue(addReq.takeProfitPip as any));
      req.setSlRefiner(this.fromDoubleValue(addReq.slRefiner as any));
      req.setTpRefiner(this.fromDoubleValue(addReq.tpRefiner as any));
      req.setBalancePercentageValue(this.fromDoubleValue(addReq.balancePercentageValue as any));
      req.setAllowedTradeSide(this.fromStringValueNull(addReq.allowedTradeSide));
      req.setMagicFilterType(addReq.magicFilterType);
      req.setCommentFilterType(addReq.commentFilterType);

      const client = this.getClient();
      client.addCopierTemplate(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  pauseAccount = (pauseReq: PauseAccountReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new PauseAccountReq();
      req.setUserId(pauseReq.userId);
      req.setConfigId(pauseReq.configId);
      if (!!pauseReq.slaveId?.value) {
        req.setSlaveId(this.fromStringValue(pauseReq.slaveId));
      }
      if (pauseReq.pauseUntil && pauseReq.pauseUntil.seconds > 0) {
        req.setPauseUntil(this.fromTimestamp(pauseReq.pauseUntil));
      }

      const client = this.getClient();
      client.pauseAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  fromBoolValue = (s: BoolValue.AsObject | undefined): BoolValue => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let boolValue = new BoolValue();
    boolValue.setValue(s?.value);
    return boolValue;
  };

  fromBoolArrayValue = (s: BoolValue.AsObject[] | undefined): BoolValue[] => {
    if (s === null || s === undefined) {
      return null as any;
    }

    let boolArray: BoolValue[] = [];
    for (const i of s) {
      let boolValue = new BoolValue();
      boolValue.setValue(i?.value);
      boolArray.push(boolValue);
    }

    return boolArray;
  };

  fromTimestamp = toTimestamp;

  fromStringValue = (s: StringValue.AsObject | undefined): StringValue => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let stringValue = new StringValue();
    stringValue.setValue(s.value);
    return stringValue;
  };

  fromStringValueNull = (s: StringValue.AsObject | undefined): StringValue => {
    if (s === null || s === undefined || !s?.value) {
      return null as any;
    }
    let stringValue = new StringValue();
    stringValue.setValue(s.value);
    return stringValue;
  };

  fromTradingHourList = (s: TradingHour.AsObject[] | undefined): TradingHour[] => {
    if (s === null || s === undefined) {
      return null as any;
    }
    const tradingHourList: TradingHour[] = [];
    for (const i of s) {
      let tradingHour = new TradingHour();
      tradingHour.setDayOfWeek(i.dayOfWeek);
      tradingHour.setStartTime(i.startTime);
      tradingHour.setEndTime(i.endTime);
      // if (i.startTime) {
      //   const timestamp = new Timestamp();
      //   timestamp.setSeconds(i.startTime);
      //   timestamp.setNanos(i.startTime);
      //   tradingHour.setStartTime(timestamp);
      // }
      // if (i.endTime) {
      //   const timestamp = new Timestamp();
      //   timestamp.setSeconds(i.endTime.seconds);
      //   timestamp.setNanos(i.endTime.nanos);
      //   tradingHour.setEndTime(timestamp);
      // }
      tradingHourList.push(tradingHour);
    }
    return tradingHourList;
  };

  fromStringValueArr = (s: StringValue.AsObject[] | undefined): StringValue[] => {
    if (s === null || s === undefined) {
      return null as any;
    }
    const stringValArr: StringValue[] = s.map((val) => {
      let stringValue = new StringValue();
      stringValue.setValue(val.value);
      return stringValue;
    });

    return stringValArr;
  };

  fromInt64ValueArr = (s: Int64Value.AsObject[] | undefined): Int64Value[] => {
    if (s === null || s === undefined) {
      return null as any;
    }
    const intValArr: Int64Value[] = s.map((val) => {
      let intValue = new Int64Value();
      intValue.setValue(val.value);
      return intValue;
    });

    return intValArr;
  };

  fromInt32Value = (s: Int32Value.AsObject | undefined): Int32Value => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let int32Value = new Int32Value();
    int32Value.setValue(s.value);
    return int32Value;
  };

  fromInt64Value = (s: Int64Value.AsObject | undefined): Int64Value => {
    if (s === null || s === undefined) {
      return null as any;
    }
    let int64Value = new Int64Value();
    int64Value.setValue(s.value);
    return int64Value;
  };

  fromMappingList = (reqList: MappingReq.AsObject[] | undefined): MappingReq[] => {
    const result = [];
    if (reqList === undefined || reqList === null) {
      return [];
    }
    for (const req of reqList) {
      const mappingReq = new MappingReq();
      mappingReq.setName(req.name);
      mappingReq.setMaster(req.master);
      result.push(mappingReq);
    }
    return result;
  };

  flattenUpdateSlaveReq = (
    updateSlaveReq: UpdateSlaveReq.AsObject,
    slaveAccount?: string,
    masterAccount?: string
  ): Record<string, string | number | boolean | null> => {
    const flatObject = {
      timestamp: new Date()?.toISOString(),
      configId: updateSlaveReq.configId,
      slaveId: updateSlaveReq.slaveId,
      enable: updateSlaveReq.enable?.value ?? null,
      riskType: risk_type_display_map[updateSlaveReq.riskType],
      fixedLotValue: updateSlaveReq.fixedLotValue?.value ? updateSlaveReq.fixedLotValue?.value : null,
      lotMultiplierValue: updateSlaveReq.lotMultiplierValue?.value ? updateSlaveReq.lotMultiplierValue?.value : null,
      balanceMultiplierValue: updateSlaveReq.balanceMultiplierValue?.value ? updateSlaveReq.balanceMultiplierValue?.value : null,
      balancePercentageValue: updateSlaveReq.balancePercentageValue?.value ? updateSlaveReq.balancePercentageValue?.value : null,
      fixedBalanceMultiplierValue: updateSlaveReq.fixedBalanceMultiplierValue?.value
        ? updateSlaveReq.fixedBalanceMultiplierValue?.value
        : null,
      fixedMasterBalance: updateSlaveReq.fixedMasterBalance?.value ? updateSlaveReq.fixedMasterBalance?.value : null,

      copyStopLoss: updateSlaveReq.copyStopLoss?.value ?? null,
      copyTakeProfit: updateSlaveReq.copyTakeProfit?.value ?? null,
      copyPendingOrders: updateSlaveReq.copyPendingOrders?.value ?? null,

      takeProfitPip: updateSlaveReq.takeProfitPip?.value ?? null,
      stopLossPip: updateSlaveReq.stopLossPip?.value ?? null,
      slRefiner: updateSlaveReq.slRefiner?.value ?? null,
      tpRefiner: updateSlaveReq.tpRefiner?.value ?? null,

      forceMinimumLotSize: updateSlaveReq.forceMinimumLotSize?.value ?? null,
      forceMaximumLotSize: updateSlaveReq.forceMaximumLotSize?.value ?? null,
      reverseTrade: updateSlaveReq.reverseTrade?.value ?? null,

      considerContractSize: updateSlaveReq.considerContractSize?.value ?? null,
      copyExpiryTime: updateSlaveReq.copyExpiryTime?.value ?? null,
      strictClose: updateSlaveReq.strictClose?.value ?? null,
      copyComment: updateSlaveReq.copyComment?.value ?? null,
      customComment: updateSlaveReq.customComment?.value ?? null,
      copyMagicNumber: updateSlaveReq.copyMagicNumber?.value ?? null,
      magicNumber: updateSlaveReq.magicNumber?.value ?? null,
      minDelayTime: updateSlaveReq.minDelayTime?.value ?? null,
      maxDelayTime: updateSlaveReq.maxDelayTime?.value ?? null,
      lotRefinerValue: updateSlaveReq.lotRefinerValue?.value ?? null,

      magicFiltersList: updateSlaveReq.magicFiltersList?.map((item) => item.value).join(',') ?? '',
      commentFiltersList: updateSlaveReq.commentFiltersList?.map((item) => item.value).join(',') ?? '',
      minLotFilter: updateSlaveReq.minLotFilter?.value ?? null,
      maxLotFilter: updateSlaveReq.maxLotFilter?.value ?? null,

      manageExisting: updateSlaveReq.manageExisting?.value ?? null
    };

    return Object.fromEntries(Object.entries(flatObject).filter(([_, v]) => v !== null && v !== undefined));
  };
}

export class BrokerClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__brokerManagerClient != null) {
      return __brokerManagerClient;
    }
    __brokerManagerClient = new BrokerManagerClient(gatewayAddress);
    return __brokerManagerClient;
  };

  listBrokers = (listBrokerReq: ListBrokersReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListBrokersReq();
      req.setPlatform(listBrokerReq.platform);
      req.setPaginationCursor(listBrokerReq.paginationCursor);
      req.setQuery(listBrokerReq.query);

      const client = this.getClient();
      client.listBrokers(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getBrokerInfo = (brokerInfoReq: GetBrokerInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetBrokerInfoReq();
      req.setId(brokerInfoReq.id);

      const client = this.getClient();
      client.getBrokerInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getServerInfo = (serverInfoReq: GetServerInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetServerInfoReq();
      req.setBrokerId(serverInfoReq.brokerId);
      req.setServerId(serverInfoReq.serverId);

      const client = this.getClient();
      client.getServerInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };
}

export class BillingClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__billingClient != null) {
      return __billingClient;
    }
    __billingClient = new BillingManagerClient(gatewayAddress);
    return __billingClient;
  };

  // Fields this method defaults below (coupon, useTrial, itemsList) are optional to
  // callers — the generated AsObject marks every proto field required, which older
  // single-price call sites (DE, upgrade/seat drawers) can't satisfy and don't need.
  createSubscription = (createSubReq: CreateSubscriptionRequest) => {
    return new Promise((resolve, reject) => {
      let req = new CreateSubscriptionReq();
      req.setUserId(createSubReq.userId);
      req.setPriceId(createSubReq.priceId);
      req.setQuantity(createSubReq.quantity);
      req.setToltReferral(createSubReq.toltReferral);
      req.setCoupon(createSubReq.coupon || '');
      req.setCheckoutRef(createSubReq.checkoutRef || '');
      // Ad attribution — opaque, forwarded unmodified. Built here rather than
      // at the ~10 call sites: every one of them sits beside a Stripe confirm,
      // and none of them cares about ad tracking. Omitted entirely when the
      // buyer did not arrive from an ad, which is the common case.
      req.setAdAttribution(buildAdAttribution(createSubReq.adAttribution));
      req.setIsDedi(createSubReq.isDedi);
      req.setUseTrial(Boolean(createSubReq.useTrial));
      const items = (createSubReq.itemsList || []).map((it) => {
        const si = new SubscriptionItem();
        si.setPriceId(it.priceId);
        si.setQuantity(it.quantity);
        return si;
      });
      req.setItemsList(items);

      const client = this.getClient();
      client.createSubscription(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  // The nested requests take the same relaxed shapes createSubscription/modifySubscriptions accept.
  createCryptoPayment = (createSubReq: CryptoPaymentRequest) => {
    return new Promise((resolve, reject) => {
      let req = new CreateCryptoPaymentRequest();
      req.setUserId(createSubReq.userId);
      req.setCreateSubscription(this.fromCreateSubscription(createSubReq.createSubscription));
      req.setModifySubscription(this.fromModiySubscription(createSubReq.modifySubscription));
      req.setPurchaseTotal(createSubReq.purchaseTotal);
      req.setCreditPurchaseTotal(createSubReq.creditPurchaseTotal);

      const client = this.getClient();
      client.createCryptoPayment(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getOverDueInfo = (createSubReq: GetOverDueInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetOverDueInfoReq();
      req.setUserId(createSubReq.userId);
      req.setSubscriptionId(createSubReq.subscriptionId);

      const client = this.getClient();
      client.getOverDueInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  renewOverDueSubscription = (createSubReq: RenewOverDueSubscriptionReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new RenewOverDueSubscriptionReq();
      req.setUserId(createSubReq.userId);
      req.setPaymentMethodId(createSubReq.paymentMethodId);
      req.setInvoiceId(createSubReq.invoiceId);

      const client = this.getClient();
      client.renewOverDueSubscription(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listSubscriptions = (listSubscriptionsReq: ListSubscriptionsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListSubscriptionsReq();
      req.setUserId(listSubscriptionsReq.userId);
      req.setStatusList(listSubscriptionsReq.statusList);

      const client = this.getClient();
      client.listSubscriptions(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  modifySubscriptions = (modifySubscriptionsReq: ModifySubscriptionRequest) => {
    return new Promise((resolve, reject) => {
      let req = new ModifySubscriptionReq();
      req.setUserId(modifySubscriptionsReq.userId);
      req.setSubscriptionId(modifySubscriptionsReq.subscriptionId);
      req.setQuantity(modifySubscriptionsReq.quantity);
      req.setSubscriptionItemId(modifySubscriptionsReq.subscriptionItemId || '');
      req.setCoupon(modifySubscriptionsReq.coupon || '');
      req.setCheckoutRef(modifySubscriptionsReq.checkoutRef || '');

      const client = this.getClient();
      client.modifySubscription(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  cancelSubscription = (cancelReq: CancelSubscriptionReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CancelSubscriptionReq();
      req.setUserId(cancelReq.userId);
      req.setSubscriptionId(cancelReq.subscriptionId);

      const client = this.getClient();
      client.cancelSubscription(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  // One unary call, resolved with its reply as a plain object.
  private unary = (
    send: (client: BillingManagerClient, meta: any, done: (err: unknown, resp: { toObject(): unknown } | null) => void) => void
  ) =>
    new Promise<any>((resolve, reject) => {
      send(this.getClient(), this.meta as any, (err, resp) => (err != null ? reject(err) : resolve(resp?.toObject())));
    });

  getSubscriptionFreezeEligibility = (eligibilityReq: GetSubscriptionFreezeEligibilityReq.AsObject) => {
    const req = new GetSubscriptionFreezeEligibilityReq();
    req.setUserId(eligibilityReq.userId);
    return this.unary((client, meta, done) => client.getSubscriptionFreezeEligibility(req, meta, done));
  };

  previewSubscriptionFreeze = (previewReq: PreviewSubscriptionFreezeReq.AsObject) => {
    const req = new PreviewSubscriptionFreezeReq();
    req.setUserId(previewReq.userId);
    return this.unary((client, meta, done) => client.previewSubscriptionFreeze(req, meta, done));
  };

  createSubscriptionFreeze = (createReq: CreateSubscriptionFreezeReq.AsObject) => {
    const req = new CreateSubscriptionFreezeReq();
    req.setUserId(createReq.userId);
    req.setSnapshotHash(createReq.snapshotHash);
    req.setIdempotencyKey(createReq.idempotencyKey);
    return this.unary((client, meta, done) => client.createSubscriptionFreeze(req, meta, done));
  };

  getSubscriptionFreeze = (getReq: GetSubscriptionFreezeReq.AsObject) => {
    const req = new GetSubscriptionFreezeReq();
    req.setUserId(getReq.userId);
    return this.unary((client, meta, done) => client.getSubscriptionFreeze(req, meta, done));
  };

  resumeSubscriptionFreeze = (resumeReq: ResumeSubscriptionFreezeReq.AsObject) => {
    const req = new ResumeSubscriptionFreezeReq();
    req.setUserId(resumeReq.userId);
    req.setFreezeId(resumeReq.freezeId);
    req.setIdempotencyKey(resumeReq.idempotencyKey);
    req.setPaymentMethodId(resumeReq.paymentMethodId);
    return this.unary((client, meta, done) => client.resumeSubscriptionFreeze(req, meta, done));
  };

  // Ends every paused plan now; the unused time stays as account credit.
  cancelSubscriptionFreeze = (cancelReq: CancelSubscriptionFreezeReq.AsObject) => {
    const req = new CancelSubscriptionFreezeReq();
    req.setUserId(cancelReq.userId);
    req.setFreezeId(cancelReq.freezeId);
    req.setExpectedVersion(cancelReq.expectedVersion);
    return this.unary((client, meta, done) => client.cancelSubscriptionFreeze(req, meta, done));
  };

  getCancelOffer = (offerReq: GetCancelOfferReq.AsObject) => {
    const req = new GetCancelOfferReq();
    req.setUserId(offerReq.userId);
    return this.unary((client, meta, done) => client.getCancelOffer(req, meta, done));
  };

  acceptCancelOffer = (acceptReq: AcceptCancelOfferReq.AsObject) => {
    const req = new AcceptCancelOfferReq();
    req.setUserId(acceptReq.userId);
    req.setIdempotencyKey(acceptReq.idempotencyKey);
    return this.unary((client, meta, done) => client.acceptCancelOffer(req, meta, done));
  };

  cancelSubscriptionImmediately = (cancelReq: CancelSubscriptionImmediatelyReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CancelSubscriptionImmediatelyReq();
      req.setUserId(cancelReq.userId);
      req.setSubscriptionId(cancelReq.subscriptionId);

      const client = this.getClient();
      client.cancelSubscriptionImmediately(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  resumeSubscription = (resumeReq: ReactivateSubscriptionReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ReactivateSubscriptionReq();
      req.setUserId(resumeReq.userId);
      req.setSubscriptionId(resumeReq.subscriptionId);

      const client = this.getClient();
      client.reactivateSubscription(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listCards = (listCardReq: ListCardsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListCardsReq();
      req.setUserId(listCardReq.userId);

      const client = this.getClient();
      client.listCards(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  setDefaultPaymentMethod = (defaultCardReq: SetDefaultPaymentMethodReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SetDefaultPaymentMethodReq();
      req.setUserId(defaultCardReq.userId);
      req.setCardId(defaultCardReq.cardId);
      req.setPaymentMethodId(defaultCardReq.paymentMethodId);

      const client = this.getClient();
      client.setDefaultPaymentMethod(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listInvoices = (invoiceReq: ListInvoicesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListInvoicesReq();
      req.setUserId(invoiceReq.userId);
      // req.setInterval(invoiceReq.interval);
      req.setPageSize(invoiceReq.pageSize);
      req.setPaginationCursor(invoiceReq.paginationCursor);

      const client = this.getClient();
      client.listInvoices(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getDownloadableInvoice = (invoiceReq: GetDownloadableInvoiceReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetDownloadableInvoiceReq();
      req.setUserId(invoiceReq.userId);
      req.setInvoiceId(invoiceReq.invoiceId);

      const client = this.getClient();
      client.getDownloadableInvoice(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  billingOverview = (invoiceReq: BillingOverviewReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new BillingOverviewReq();
      req.setUserId(invoiceReq.userId);

      const client = this.getClient();
      client.billingOverview(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getUpcomingInvoice = (invoiceReq: GetUpcomingInvoiceReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetUpcomingInvoiceReq();
      req.setUserId(invoiceReq.userId);
      req.setQuantity(invoiceReq.quantity);
      req.setPriceId(invoiceReq.priceId);
      req.setInvoiceType(invoiceReq.invoiceType);
      req.setSubscriptionId(invoiceReq.subscriptionId);
      req.setCoupon(invoiceReq.coupon);

      const client = this.getClient();
      client.getUpcomingInvoice(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  previewCheckout = (previewReq: PreviewCheckoutReq.AsObject) => {
    return new Promise<any>((resolve, reject) => {
      let req = new PreviewCheckoutReq();
      req.setUserId(previewReq.userId);
      req.setCoupon(previewReq.coupon || '');
      req.setUseTrial(!!previewReq.useTrial);
      req.setSkipCancelOffer(!!previewReq.skipCancelOffer);

      (previewReq.newSubsList || []).forEach((n) => {
        const ns = new NewSubscriptionPreview();
        ns.setPriceId(n.priceId || '');
        ns.setQuantity(n.quantity || 0);
        ns.setDedicated(!!n.dedicated);
        req.addNewSubs(ns);
      });

      (previewReq.updatesList || []).forEach((u) => {
        const update = new SubscriptionUpdate();
        update.setSubscriptionId(u.subscriptionId || '');
        (u.itemsList || []).forEach((d) => {
          const item = new SubscriptionItemDelta();
          item.setPriceId(d.priceId || '');
          item.setQuantity(d.quantity || 0);
          item.setItemId(d.itemId || '');
          item.setRemove(!!d.remove);
          update.addItems(item);
        });
        req.addUpdates(update);
      });

      const client = this.getClient();
      client.previewCheckout(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  listPrices = (pricesReq: ListPricesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListPricesReq();
      req.setUserId(pricesReq.userId);

      const client = this.getClient();
      client.listPrices(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  cardSetUpIntent = (invoiceReq: CardSetUpIntentReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CardSetUpIntentReq();
      req.setUserId(invoiceReq.userId);

      const client = this.getClient();
      client.cardSetUpIntent(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteCard = (deleteReq: DeleteCardReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteCardReq();
      req.setUserId(deleteReq.userId);
      req.setCardId(deleteReq.cardId);

      const client = this.getClient();
      client.deleteCard(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  addSubscriptionItem = (addReq: AddSubscriptionItemReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AddSubscriptionItemReq();
      req.setUserId(addReq.userId);
      req.setSubscriptionId(addReq.subscriptionId);
      req.setPriceId(addReq.priceId);
      req.setQuantity(addReq.quantity);
      req.setCoupon(addReq.coupon || '');
      req.setCheckoutRef(addReq.checkoutRef || '');

      const client = this.getClient();
      client.addSubscriptionItem(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  removeSubscriptionItem = (removeReq: RemoveSubscriptionItemReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new RemoveSubscriptionItemReq();
      req.setUserId(removeReq.userId);
      req.setSubscriptionId(removeReq.subscriptionId);
      req.setSubscriptionItemId(removeReq.subscriptionItemId);

      const client = this.getClient();
      client.removeSubscriptionItem(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  hasPendingCryptoPayment = (pendingReq: HasPendingCryptoPaymentRequest.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new HasPendingCryptoPaymentRequest();
      req.setUserId(pendingReq.userId);

      const client = this.getClient();
      client.hasPendingCryptoPayment(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  cancelCryptoPayment = (cancelReq: CancelCryptoPaymentRequest.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CancelCryptoPaymentRequest();
      req.setUserId(cancelReq.userId);
      req.setPaymentId(cancelReq.paymentId);

      const client = this.getClient();
      client.cancelCryptoPayment(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  fromModiySubscription = (req: ModifySubscriptionRequest | undefined): ModifySubscriptionReq => {
    if (req === undefined || req === null) {
      return null as any;
    }
    const modifyReq = new ModifySubscriptionReq();
    modifyReq.setUserId(req.userId);
    modifyReq.setQuantity(req.quantity);
    modifyReq.setSubscriptionId(req.subscriptionId);
    modifyReq.setSubscriptionItemId(req.subscriptionItemId || '');
    modifyReq.setPriceId(req.priceId || '');
    modifyReq.setCoupon(req.coupon || '');
    modifyReq.setCheckoutRef(req.checkoutRef || '');
    return modifyReq;
  };

  fromCreateSubscription = (req: CreateSubscriptionRequest | undefined): CreateSubscriptionReq => {
    if (req === undefined || req === null) {
      return null as any;
    }
    const createReq = new CreateSubscriptionReq();
    createReq.setUserId(req.userId);
    createReq.setQuantity(req.quantity);
    createReq.setIsDedi(req.isDedi);
    createReq.setToltReferral(req.toltReferral);
    createReq.setCoupon(req.coupon || '');
    createReq.setCheckoutRef(req.checkoutRef || '');
    createReq.setAdAttribution(buildAdAttribution(req.adAttribution));
    createReq.setPriceId(req.priceId);
    createReq.setUseTrial(Boolean(req.useTrial));
    const items = (req.itemsList || []).map((it) => {
      const si = new SubscriptionItem();
      si.setPriceId(it.priceId);
      si.setQuantity(it.quantity);
      return si;
    });
    createReq.setItemsList(items);
    return createReq;
  };
}

export class AnalyticsClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__analyticsManagerClient != null) {
      return __analyticsManagerClient;
    }
    __analyticsManagerClient = new AnalyticsManagerClient(gatewayAddress);
    return __analyticsManagerClient;
  };

  listTrades = (listTradesReq: ListTradesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListTradesReq();
      req.setAccountId(listTradesReq.accountId);
      req.setUserId(listTradesReq.userId);
      req.setPaginationCursor(listTradesReq.paginationCursor);
      req.setPageSize(listTradesReq.pageSize);
      req.setStatus(listTradesReq.status);
      req.setSearchKey(listTradesReq.searchKey);

      const client = this.getClient();
      client.listTrades(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  overviewStats = (overviewStatsReq: OverviewStatsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new OverviewStatsReq();
      req.setUserId(overviewStatsReq.userId);
      req.setCurrency(overviewStatsReq.currency);

      const client = this.getClient();
      client.overviewStats(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listAccountStats = (listAccountReq: ListAccountStatsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListAccountStatsReq();
      req.setUserId(listAccountReq.userId);
      req.setAccountIdsList(listAccountReq.accountIdsList);

      const client = this.getClient();
      client.listAccountStats(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  accountStats = (accountStatsReq: AccountStatsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AccountStatsReq();
      req.setUserId(accountStatsReq.userId);
      req.setAccountId(accountStatsReq.accountId);

      const client = this.getClient();
      client.accountStats(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  copierMetrics = (copierMatricsReq: CopierMetricsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CopierMetricsReq();
      req.setUserId(copierMatricsReq.userId);
      req.setCurrency(copierMatricsReq.currency);

      const client = this.getClient();
      client.copierMetrics(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getBalanceInfo = (balanceInfoReq: GetBalanceInfoReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetBalanceInfoReq();
      req.setUserId(balanceInfoReq.userId);
      req.setAccountsList(balanceInfoReq.accountsList);

      const client = this.getClient();
      client.getBalanceInfo(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  accountsOverviewStats = (accountsOverviewStatsReq: AccountsOverviewStatsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new AccountsOverviewStatsReq();
      req.setUserId(accountsOverviewStatsReq.userId);
      req.setCurrency(accountsOverviewStatsReq.currency);

      const client = this.getClient();
      client.accountsOverviewStats(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };
}

export class NewsClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__newsManagerClient != null) {
      return __newsManagerClient;
    }
    __newsManagerClient = new NewsManagerClient(gatewayAddress);
    return __newsManagerClient;
  };

  getCalendarEventList = (calendarEventsReq: CalendarEventListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CalendarEventListReq();
      req.setUserId(calendarEventsReq.userId);
      req.setRegion(calendarEventsReq.region);
      req.setImpact(calendarEventsReq.impact);
      req.setFromDate(calendarEventsReq.fromDate);
      req.setToDate(calendarEventsReq.toDate);
      req.setQuery(calendarEventsReq.query);
      req.setPage(calendarEventsReq.page);
      req.setPageSize(calendarEventsReq.pageSize);

      const client = this.getClient();
      client.getCalendarEventList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getMarketNewsList = (newsReq: MarketNewsListReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new MarketNewsListReq();
      req.setUserId(newsReq.userId);
      req.setQuery(newsReq.query);
      req.setSourcesList(newsReq.sourcesList);
      req.setCountriesList(newsReq.countriesList);
      req.setImpact(newsReq.impact);
      req.setSentiment(newsReq.sentiment);
      req.setAssetClassesList(newsReq.assetClassesList);
      req.setBookmarkedOnly(newsReq.bookmarkedOnly);
      req.setFromDate(newsReq.fromDate);
      req.setToDate(newsReq.toDate);
      req.setPage(newsReq.page);
      req.setPageSize(newsReq.pageSize);
      req.setSortBy(newsReq.sortBy);
      const client = this.getClient();
      client.getMarketNewsList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  toggleNewsBookmark = (reqObj: ToggleNewsBookmarkReq) => {
    return new Promise<ToggleNewsBookmarkReply.AsObject>((resolve, reject) => {
      const client = this.getClient();
      client.toggleNewsBookmark(reqObj, this.meta as any, (err, resp) => {
        if (err) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  listMyAlerts = (alertsReq: ListMyAlertsReq.AsObject) => {
    return new Promise<ListMyAlertsReply.AsObject>((resolve, reject) => {
      let req = new ListMyAlertsReq();
      req.setUserId(alertsReq.userId);
      req.setOnlyUnmuted(alertsReq.onlyUnmuted);
      req.setPage(alertsReq.page);
      req.setPageSize(alertsReq.pageSize);

      const client = this.getClient();
      client.listMyAlerts(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  createCalendarEventAlert = (alertReq: CreateCalendarEventAlertReq.AsObject) => {
    return new Promise<CreateCalendarEventAlertReply.AsObject>((resolve, reject) => {
      let req = new CreateCalendarEventAlertReq();
      req.setUserId(alertReq.userId);
      req.setCountry(alertReq.country);
      req.setEventName(alertReq.eventName);

      const client = this.getClient();
      client.createCalendarEventAlert(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };

  deleteCalendarEventAlert = (reqObj: DeleteCalendarEventAlertReq.AsObject) => {
    return new Promise<DeleteCalendarEventAlertReply.AsObject>((resolve, reject) => {
      let req = new DeleteCalendarEventAlertReq();
      req.setUserId(reqObj.userId);
      req.setAlertId(reqObj.alertId);

      const client = this.getClient();
      client.deleteCalendarEventAlert(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp!.toObject());
        }
      });
    });
  };
}

export class NotificationClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__notificationClient != null) {
      return __notificationClient;
    }
    __notificationClient = new NotificationManagerClient(gatewayAddress);
    return __notificationClient;
  };

  getNotifications = (configReq: GetNotificationsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetNotificationsReq();
      req.setUserId(configReq.userId);
      req.setType(configReq.type);

      const client = this.getClient();
      client.getNotifications(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getNotification = (configReq: GetNotificationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetNotificationReq();
      req.setEvent(configReq.event);
      req.setType(configReq.type);
      req.setUserId(configReq.userId);

      const client = this.getClient();
      client.getNotification(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getConfigDetails = (configReq: ConfigDetailsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ConfigDetailsReq();
      req.setUserId(configReq.userId);
      req.setNotificationId(configReq.notificationId);
      req.setEventType(configReq.eventType);

      const client = this.getClient();
      client.getConfigDetails(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  overrideNotification = (configReq: OverrideNotificationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new OverrideNotificationReq();
      req.setEvent(configReq.event);
      req.setType(configReq.type);
      req.setUserId(configReq.userId);
      req.setAccountId(configReq.accountId);
      req.setSubject(configReq.subject);
      req.setMessageTemplate(configReq.messageTemplate);
      req.setParentRevision(configReq.parentRevision);

      const client = this.getClient();
      client.overrideNotification(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  enableNotification = (configReq: EnableNotificationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EnableNotificationReq();
      req.setEvent(configReq.event);
      req.setType(configReq.type);
      req.setUserId(configReq.userId);
      req.setAccountId(configReq.accountId);

      const client = this.getClient();
      client.enableNotification(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  disableNotification = (configReq: DisableNotificationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DisableNotificationReq();
      req.setEvent(configReq.event);
      req.setType(configReq.type);
      req.setUserId(configReq.userId);

      const client = this.getClient();
      client.disableNotification(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  blockAccount = (blockReq: BlockNotificationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new BlockNotificationReq();
      req.setUserId(blockReq.userId);
      req.setEvent(blockReq.event);
      req.setAccountId(blockReq.accountId);

      const client = this.getClient();
      client.blockAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  unblockAccount = (unblockReq: UnblockNotificationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UnblockNotificationReq();
      req.setUserId(unblockReq.userId);
      req.setEvent(unblockReq.event);
      req.setAccountId(unblockReq.accountId);

      const client = this.getClient();
      client.unblockAccount(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  editConfig = (editReq: EditConfigReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EditConfigReq();
      req.setUserId(editReq.userId);

      // Set notification config
      if (editReq.notificationConfig) {
        const config = new NotificationConfig();
        config.setUserId(editReq.notificationConfig.userId);
        config.setEventType(editReq.notificationConfig.eventType);
        config.setSubject(editReq.notificationConfig.subject);
        config.setNotificationType(editReq.notificationConfig.notificationType);
        config.setNotificationConfigId(editReq.notificationConfig.notificationConfigId);
        config.setMessageTemplate(editReq.notificationConfig.messageTemplate);
        config.setUserConfig(editReq.notificationConfig.userConfig);
        config.setEnabled(editReq.notificationConfig.enabled);
        req.setNotificationConfig(config);
      }

      // Set block list
      if (editReq.blockListList) {
        for (const acc of editReq.blockListList) {
          const meta = new AccountMeta();
          meta.setAccountId(acc.accountId);
          meta.setAccountName(acc.accountName);
          meta.setAccountConfigId(acc.accountConfigId);
          req.addBlockList(meta);
        }
      }

      // Set unblock list
      if (editReq.unblockListList) {
        for (const acc of editReq.unblockListList) {
          const meta = new AccountMeta();
          meta.setAccountId(acc.accountId);
          meta.setAccountName(acc.accountName);
          meta.setAccountConfigId(acc.accountConfigId);
          req.addUnblockList(meta);
        }
      }

      const client = this.getClient();
      client.editConfig(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getIntegrationStatus = (integrationReq: IntegrationStatusReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new IntegrationStatusReq();
      req.setUserId(integrationReq.userId);
      // req.setDefaultEmail(configReq.defaultEmail);

      const client = this.getClient();
      client.getIntegrationStatus(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  installIntegration = (installReq: InstallIntegrationReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new InstallIntegrationReq();
      req.setUserId(installReq.userId);
      req.setType(installReq.type);

      const client = this.getClient();
      client.installIntegration(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  editNotificationEmail = (editReq: EditNotificationEmailReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new EditNotificationEmailReq();
      req.setUserId(editReq.userId);
      req.setNotificationEmail(editReq.notificationEmail);

      const client = this.getClient();
      client.editNotificationEmail(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  fromNotificationConfig = (req: NotificationConfig.AsObject | undefined): NotificationConfig => {
    if (req === undefined || req === null) {
      return null as any;
    }
    const notificationConfig = new NotificationConfig();
    notificationConfig.setUserId(req.userId);
    notificationConfig.setEnabled(req.enabled);
    notificationConfig.setUserConfig(req.userConfig);
    notificationConfig.setNotificationConfigId(req.notificationConfigId);
    notificationConfig.setNotificationType(req.notificationType);
    notificationConfig.setEventType(req.eventType);
    notificationConfig.setMessageTemplate(req.messageTemplate);
    notificationConfig.setSubject(req.subject);
    return notificationConfig;
  };

  fromAccountMeta = (reqList: AccountMeta.AsObject[] | undefined): AccountMeta[] => {
    const result: AccountMeta[] = [];
    if (reqList === undefined || reqList === null) {
      return null as any;
    }

    for (const req of reqList) {
      const accountMeta = new AccountMeta();
      accountMeta.setAccountId(req.accountId);
      accountMeta.setAccountConfigId(req.accountConfigId);
      accountMeta.setAccountName(req.accountName);
      result.push(accountMeta);
    }

    return result;
  };

  fromChannelMeta = (reqList: ChannelMeta.AsObject[] | undefined): ChannelMeta[] => {
    const result: ChannelMeta[] = [];
    if (reqList === undefined || reqList === null) {
      return null as any;
    }
    for (const req of reqList) {
      const channelMeta = new ChannelMeta();
      channelMeta.setChannelConfigId(req.channelConfigId);
      channelMeta.setChannelName(req.channelName);
      result.push(channelMeta);
    }
    return result;
  };

  // ── Telegram Bot ──────────────────────────────────────────────────────────

  addBot = (botReq: AddBotReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new AddBotReq();
      req.setUserId(botReq.userId);
      req.setBotName(botReq.botName);
      req.setBotDescription(botReq.botDescription);
      req.setBotToken(botReq.botToken);
      req.setNotificationType(botReq.notificationType);
      this.getClient().addBot(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  getBots = (botReq: GetBotsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetBotsReq();
      req.setUserId(botReq.userId);
      req.setNotificationType(botReq.notificationType);
      this.getClient().getBots(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editBot = (botReq: EditBotReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditBotReq();
      req.setUserId(botReq.userId);
      req.setBotConfigId(botReq.botConfigId);
      req.setBotName(botReq.botName);
      req.setBotDescription(botReq.botDescription);
      req.setBotToken(botReq.botToken);
      this.getClient().editBot(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editBotStatus = (botReq: EditBotStatusReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditBotStatusReq();
      req.setUserId(botReq.userId);
      req.setBotConfigId(botReq.botConfigId);
      req.setNotificationType(botReq.notificationType);
      req.setEnable(botReq.enable);
      this.getClient().editBotStatus(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  deleteBot = (botReq: DeleteBotReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new DeleteBotReq();
      req.setUserId(botReq.userId);
      req.setBotConfigId(botReq.botConfigId);
      req.setNotificationType(botReq.notificationType);
      this.getClient().deleteBots(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  // ── Telegram Channel ──────────────────────────────────────────────────────

  addChannel = (chReq: AddChannelReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new AddChannelReq();
      req.setUserId(chReq.userId);
      req.setChannelName(chReq.channelName);
      req.setChannelDescription(chReq.channelDescription);
      req.setChannelId(chReq.channelId);
      this.getClient().addChannel(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  getChannel = (chReq: GetChannelReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetChannelReq();
      req.setUserId(chReq.userId);
      this.getClient().getChannel(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editChannel = (chReq: EditChannelReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditChannelReq();
      req.setUserId(chReq.userId);
      req.setChannelConfigId(chReq.channelConfigId);
      req.setChannelName(chReq.channelName);
      req.setChannelDescription(chReq.channelDescription);
      req.setChannelId(chReq.channelId);
      this.getClient().editChannel(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  deleteChannel = (chReq: DeleteChannelReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new DeleteChannelReq();
      req.setUserId(chReq.userId);
      req.setChannelConfigId(chReq.channelConfigId);
      this.getClient().deleteChannel(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editChannelStatus = (chReq: EditChannelStatusReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditChannelStatusReq();
      req.setUserId(chReq.userId);
      req.setChannelConfigId(chReq.channelConfigId);
      req.setEnabled(chReq.enabled);
      this.getClient().editChannelStatus(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  // ── Telegram Bot ↔ Channel linking ───────────────────────────────────────

  linkBotToChannel = (linkReq: LinkBotToChannelReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new LinkBotToChannelReq();
      req.setUserId(linkReq.userId);
      req.setBotConfigId(linkReq.botConfigId);
      req.setChannelConfigId(linkReq.channelConfigId);
      this.getClient().linkBotToChannel(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  unlinkBotFromChannel = (unlinkReq: UnlinkBotFromChannelReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new UnlinkBotFromChannelReq();
      req.setUserId(unlinkReq.userId);
      req.setBotConfigId(unlinkReq.botConfigId);
      req.setChannelConfigId(unlinkReq.channelConfigId);
      this.getClient().unlinkBotFromChannel(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  getBotChannelLinks = (linksReq: GetBotChannelLinksReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetBotChannelLinksReq();
      req.setUserId(linksReq.userId);
      req.setBotConfigId(linksReq.botConfigId);
      req.setChannelConfigId(linksReq.channelConfigId);
      this.getClient().getBotChannelLinks(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  // ── Telegram Bot Event Config ─────────────────────────────────────────────

  getBotEventConfigs = (evReq: GetBotEventConfigsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetBotEventConfigsReq();
      req.setUserId(evReq.userId);
      req.setBotConfigId(evReq.botConfigId);
      req.setNotificationType(evReq.notificationType);
      this.getClient().getBotEventConfigs(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  getBotEventDetails = (evReq: GetBotEventDetailsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetBotEventDetailsReq();
      req.setUserId(evReq.userId);
      req.setBotConfigId(evReq.botConfigId);
      req.setNotificationConfigId(evReq.notificationConfigId);
      req.setEventType(evReq.eventType);
      this.getClient().getBotEventDetails(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editBotEventDetails = (evReq: EditBotEventDetailsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditBotEventDetailsReq();
      req.setUserId(evReq.userId);
      req.setBotConfigId(evReq.botConfigId);
      if (evReq.accountBlockListList) {
        req.setAccountBlockListList(this.fromAccountMeta(evReq.accountBlockListList));
      }
      if (evReq.accountEnabledListList) {
        req.setAccountEnabledListList(this.fromAccountMeta(evReq.accountEnabledListList));
      }
      if (evReq.channelBlockListList) {
        req.setChannelBlockListList(this.fromChannelMeta(evReq.channelBlockListList));
      }
      if (evReq.channelEnabledListList) {
        req.setChannelEnabledListList(this.fromChannelMeta(evReq.channelEnabledListList));
      }
      if (evReq.notificationConfig) {
        req.setNotificationConfig(this.fromNotificationConfig(evReq.notificationConfig));
      }
      this.getClient().editBotEventDetails(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editBotEventStatus = (evReq: EditBotEventStatusReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditBotEventStatusReq();
      req.setUserId(evReq.userId);
      req.setBotConfigId(evReq.botConfigId);
      req.setNotificationConfigId(evReq.notificationConfigId);
      req.setEnabled(evReq.enabled);
      req.setEventType(evReq.eventType);
      req.setNotificationType(evReq.notificationType);
      this.getClient().editBotEventStatus(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  // ── Telegram Routes ───────────────────────────────────────────────────────

  addTelegramRoute = (routeReq: AddTelegramRouteReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new AddTelegramRouteReq();
      req.setUserId(routeReq.userId);
      req.setEventType(routeReq.eventType);
      req.setAccountId(routeReq.accountId);
      req.setTelegramBotId(routeReq.telegramBotId);
      req.setTelegramChannelId(routeReq.telegramChannelId);
      req.setEnabled(routeReq.enabled);
      this.getClient().addTelegramRoute(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  getTelegramRoutes = (routeReq: GetTelegramRoutesReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetTelegramRoutesReq();
      req.setUserId(routeReq.userId);
      req.setEventType(routeReq.eventType);
      req.setAccountId(routeReq.accountId);
      this.getClient().getTelegramRoutes(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  deleteTelegramRoute = (routeReq: DeleteTelegramRouteReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new DeleteTelegramRouteReq();
      req.setUserId(routeReq.userId);
      req.setRouteId(routeReq.routeId);
      this.getClient().deleteTelegramRoute(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };

  editTelegramRouteStatus = (routeReq: EditTelegramRouteStatusReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new EditTelegramRouteStatusReq();
      req.setUserId(routeReq.userId);
      req.setRouteId(routeReq.routeId);
      req.setEnabled(routeReq.enabled);
      this.getClient().editTelegramRouteStatus(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          resolve(resp?.toObject());
        }
      });
    });
  };
}

export class StatusClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }

  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }

  getClient = () => {
    if (__statusManagerClient != null) {
      return __statusManagerClient;
    }
    __statusManagerClient = new StatusManagerClient(gatewayAddress);
    return __statusManagerClient;
  };

  getStatus = () => {
    return new Promise((resolve, reject) => {
      let req = new StatusReq();

      const client = this.getClient();
      client.getStatus(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };
}

export class SentimentClient {
  meta: grpc.Metadata.HeaderObject = {};

  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__sentimentClient != null) {
      return __sentimentClient;
    }
    __sentimentClient = new SentimentAnalyzerClient(gatewayAddress);
    return __sentimentClient;
  };

  getSymbolsSentiment = (sentimentReq: GetSymbolsSentimentReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetSymbolsSentimentReq();
      req.setUserId(sentimentReq.userId);
      req.setQuery(sentimentReq.query);
      req.setPageSize(sentimentReq.pageSize);
      req.setPaginationCursor(sentimentReq.paginationCursor);
      req.setSentimentTrendFilter(sentimentReq.sentimentTrendFilter);
      const client = this.getClient();
      client.getSymbolsSentiment(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getBrokersSymbolSentiment = (brokerReq: GetBrokersSymbolSentimentReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetBrokersSymbolSentimentReq();
      req.setUserId(brokerReq.userId);
      req.setSymbol(brokerReq.symbol);
      req.setPageSize(brokerReq.pageSize);
      req.setPaginationCursor(brokerReq.paginationCursor);
      const client = this.getClient();
      client.getBrokersSymbolSentiment(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getSymbols = (symbolsReq: GetSymbolsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetSymbolsReq();
      req.setUserId(symbolsReq.userId);
      req.setFilter(symbolsReq.filter);
      const client = this.getClient();
      client.getSymbols(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };
  sentimentOverview = (overviewReq: SentimentOverviewReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SentimentOverviewReq();
      req.setUserId(overviewReq.userId);

      const client = this.getClient();

      client.sentimentOverview(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };
}

export class AnalyzerServiceClient {
  meta: grpc.Metadata.HeaderObject = {};
  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__analyzerClient != null) {
      return __analyzerClient;
    }
    __analyzerClient = new AnalyzerClient(gatewayAddress);
    return __analyzerClient;
  };

  getAccountTradeList = (tradeReq: AccountTradeListRequest) => {
    return new Promise((resolve, reject) => {
      let req = new GetAccountTradeListRequest();
      req.setUserId(tradeReq.userId);
      req.setAccountId(tradeReq.accountId);
      req.setLimit(tradeReq.limit);
      req.setSortBy(tradeReq.sortBy);
      req.setSortDesc(tradeReq.sortDesc);

      if (tradeReq.page) {
        req.setPage(tradeReq.page);
      }

      if (tradeReq.search) {
        req.setSearch(tradeReq.search);
      }

      if (tradeReq.dateRange) {
        const dateRange = new DateRange();

        if (tradeReq.dateRange.startDate) {
          const startTimestamp = new Timestamp();
          startTimestamp.setSeconds(tradeReq.dateRange.startDate.seconds);
          startTimestamp.setNanos(tradeReq.dateRange.startDate.nanos);
          dateRange.setStartDate(startTimestamp);
        }

        if (tradeReq.dateRange.endDate) {
          const endTimestamp = new Timestamp();
          endTimestamp.setSeconds(tradeReq.dateRange.endDate.seconds);
          endTimestamp.setNanos(tradeReq.dateRange.endDate.nanos);
          dateRange.setEndDate(endTimestamp);
        }

        if (tradeReq.dateRange.days) {
          dateRange.setDays(tradeReq.dateRange.days);
        }

        req.setDateRange(dateRange);
      }

      const client = this.getClient();
      client.getAccountTradeList(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listAccounts = (accountReq: ListAccountsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListAccountsReq();
      req.setUserId(accountReq.userId);
      if (accountReq.accountType) req.setAccountType(accountReq.accountType);
      if (accountReq.sortBy) req.setSortBy(accountReq.sortBy);
      if (accountReq.sortOrder) req.setSortOrder(accountReq.sortOrder);
      if (accountReq.page) req.setPage(accountReq.page);
      if (accountReq.pageSize) req.setPageSize(accountReq.pageSize);
      if (accountReq.searchQuery) req.setSearchQuery(accountReq.searchQuery);

      const client = this.getClient();
      client.listAccounts(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  listAccountsLite = (accountReq: ListAccountsLiteReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new ListAccountsLiteReq();
      req.setUserId(accountReq.userId);
      if (accountReq.accountType) req.setAccountType(accountReq.accountType);

      const client = this.getClient();
      client.listAccountsLite(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getPerformanceOverview = (perfReq: GetPerformanceOverviewReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetPerformanceOverviewReq();
      req.setUserId(perfReq.userId);
      req.setAccountId(perfReq.accountId);
      if (perfReq.currency) req.setCurrency(perfReq.currency);

      if (perfReq.dateRange) {
        const dateRange = new DateRange();

        if (perfReq.dateRange.startDate) {
          const startTimestamp = new Timestamp();
          startTimestamp.setSeconds(perfReq.dateRange.startDate.seconds);
          startTimestamp.setNanos(perfReq.dateRange.startDate.nanos);
          dateRange.setStartDate(startTimestamp);
        }

        if (perfReq.dateRange.endDate) {
          const endTimestamp = new Timestamp();
          endTimestamp.setSeconds(perfReq.dateRange.endDate.seconds);
          endTimestamp.setNanos(perfReq.dateRange.endDate.nanos);
          dateRange.setEndDate(endTimestamp);
        }

        if (perfReq.dateRange.days) {
          dateRange.setDays(perfReq.dateRange.days);
        }

        req.setDateRange(dateRange);
      }

      const client = this.getClient();
      client.getPerformanceOverview(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getAccountPerformance = (reqObj: GetAccountPerformanceRequest.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetAccountPerformanceRequest();
      req.setUserId(reqObj.userId);
      req.setAccountId(reqObj.accountId);
      if (reqObj.currency) req.setCurrency(reqObj.currency);

      if (reqObj.dateRange) {
        const dateRange = new DateRange();

        if (reqObj.dateRange.startDate) {
          const startTimestamp = new Timestamp();
          startTimestamp.setSeconds(reqObj.dateRange.startDate.seconds);
          startTimestamp.setNanos(reqObj.dateRange.startDate.nanos);
          dateRange.setStartDate(startTimestamp);
        }

        if (reqObj.dateRange.endDate) {
          const endTimestamp = new Timestamp();
          endTimestamp.setSeconds(reqObj.dateRange.endDate.seconds);
          endTimestamp.setNanos(reqObj.dateRange.endDate.nanos);
          dateRange.setEndDate(endTimestamp);
        }

        if (reqObj.dateRange.days) {
          dateRange.setDays(reqObj.dateRange.days);
        }

        req.setDateRange(dateRange);
      }

      const client = this.getClient();
      client.getAccountPerformance(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getPortfolioOverview = (reqObj: GetPortfolioOverviewReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetPortfolioOverviewReq();
      req.setUserId(reqObj.userId);
      if (reqObj.currency) req.setCurrency(reqObj.currency);

      const client = this.getClient();
      client.getPortfolioOverview(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getPortfolioEquityCurve = (reqObj: GetPortfolioEquityCurveReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetPortfolioEquityCurveReq();
      req.setUserId(reqObj.userId);
      if (reqObj.currency) req.setCurrency(reqObj.currency);

      if (reqObj.dateRange) {
        const dateRange = new DateRange();
        if (reqObj.dateRange.startDate) {
          const startTs = new Timestamp();
          startTs.setSeconds(reqObj.dateRange.startDate.seconds);
          startTs.setNanos(reqObj.dateRange.startDate.nanos);
          dateRange.setStartDate(startTs);
        }
        if (reqObj.dateRange.endDate) {
          const endTs = new Timestamp();
          endTs.setSeconds(reqObj.dateRange.endDate.seconds);
          endTs.setNanos(reqObj.dateRange.endDate.nanos);
          dateRange.setEndDate(endTs);
        }
        if (reqObj.dateRange.days) {
          dateRange.setDays(reqObj.dateRange.days);
        }
        req.setDateRange(dateRange);
      }

      const client = this.getClient();
      client.getPortfolioEquityCurve(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  getPortfolioDailyAndCumulativeROI = (reqObj: GetPortfolioDailyAndCumulativeROIReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetPortfolioDailyAndCumulativeROIReq();
      req.setUserId(reqObj.userId);

      if (reqObj.dateRange) {
        const dateRange = new DateRange();
        if (reqObj.dateRange.startDate) {
          const startTs = new Timestamp();
          startTs.setSeconds(reqObj.dateRange.startDate.seconds);
          startTs.setNanos(reqObj.dateRange.startDate.nanos);
          dateRange.setStartDate(startTs);
        }
        if (reqObj.dateRange.endDate) {
          const endTs = new Timestamp();
          endTs.setSeconds(reqObj.dateRange.endDate.seconds);
          endTs.setNanos(reqObj.dateRange.endDate.nanos);
          dateRange.setEndDate(endTs);
        }
        if (reqObj.dateRange.days) {
          dateRange.setDays(reqObj.dateRange.days);
        }
        req.setDateRange(dateRange);
      }

      const client = this.getClient();
      client.getPortfolioDailyAndCumulativeROI(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  // Tag Management Methods
  getUserTags = (reqObj: GetUserTagsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetUserTagsReq();
      req.setUserId(reqObj.userId);

      const client = this.getClient();
      client.getUserTags(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  createUserTag = (reqObj: CreateUserTagReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new CreateUserTagReq();
      req.setUserId(reqObj.userId);
      req.setName(reqObj.name);
      if (reqObj.color) req.setColor(reqObj.color);

      const client = this.getClient();
      client.createUserTag(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  deleteUserTag = (reqObj: DeleteUserTagReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new DeleteUserTagReq();
      req.setUserId(reqObj.userId);
      req.setTagId(reqObj.tagId);

      const client = this.getClient();
      client.deleteUserTag(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  setTradeTag = (reqObj: SetTradeTagReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new SetTradeTagReq();
      req.setUserId(reqObj.userId);
      req.setAccountId(reqObj.accountId);
      req.setTradeId(reqObj.tradeId);
      req.setTagId(reqObj.tagId);

      const client = this.getClient();
      client.setTradeTag(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateUserTag = (reqObj: UpdateUserTagReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateUserTagReq();
      req.setUserId(reqObj.userId);
      req.setTagId(reqObj.tagId);
      req.setName(reqObj.name);
      req.setColor(reqObj.color);

      const client = this.getClient();
      client.updateUserTag(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  // Account Settings Methods
  getAccountSettings = (reqObj: GetAccountSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new GetAccountSettingsReq();
      req.setUserId(reqObj.userId);
      req.setAccountId(reqObj.accountId);

      const client = this.getClient();
      client.getAccountSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };

  updateAccountSettings = (reqObj: UpdateAccountSettingsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      let req = new UpdateAccountSettingsReq();
      req.setUserId(reqObj.userId);
      req.setAccountId(reqObj.accountId);

      if (reqObj.alertSettings) {
        const settings = new AccountAlertSettingsProto();
        settings.setRiskAlertsEnabled(reqObj.alertSettings.riskAlertsEnabled);
        settings.setMarginAlertsEnabled(reqObj.alertSettings.marginAlertsEnabled);
        settings.setOpportunityAlertsEnabled(reqObj.alertSettings.opportunityAlertsEnabled);
        settings.setDailyLossLimitPct(reqObj.alertSettings.dailyLossLimitPct);
        settings.setMaxLotSize(reqObj.alertSettings.maxLotSize);
        settings.setMaxDrawdownPct(reqObj.alertSettings.maxDrawdownPct);
        settings.setProfitTargetPct(reqObj.alertSettings.profitTargetPct);
        settings.setMarginLevelThreshold(reqObj.alertSettings.marginLevelThreshold);
        settings.setExpectancyGoalR(reqObj.alertSettings.expectancyGoalR);
        req.setAlertSettings(settings);
      }

      const client = this.getClient();
      client.updateAccountSettings(req, this.meta as any, (err, resp) => {
        if (err != null) {
          reject(err);
        } else {
          let obj = resp?.toObject();
          resolve(obj);
        }
      });
    });
  };
}

export class FeatureRequestServiceClient {
  meta: grpc.Metadata.HeaderObject = {};
  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__featureRequestProtoClient != null) {
      return __featureRequestProtoClient;
    }
    __featureRequestProtoClient = new FeatureRequestProtoClient(gatewayAddress);
    return __featureRequestProtoClient;
  };

  createPost = (reqObj: CreatePostReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new CreatePostReq();
      req.setUserId(reqObj.userId);
      req.setUserName(reqObj.userName);
      req.setTitle(reqObj.title);
      req.setDescription(reqObj.description);
      const client = this.getClient();
      client.createPost(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  listPosts = (reqObj: ListPostsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new ListPostsReq();
      req.setUserId(reqObj.userId);
      req.setView(reqObj.view);
      req.setPageSize(reqObj.pageSize);
      req.setPage(reqObj.page);
      if (reqObj.query?.value) {
        const sv = new StringValue();
        sv.setValue(reqObj.query.value);
        req.setQuery(sv);
      }
      const client = this.getClient();
      client.listPosts(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  getPost = (reqObj: GetPostReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new GetPostReq();
      req.setUserId(reqObj.userId);
      req.setPostId(reqObj.postId);
      const client = this.getClient();
      client.getPost(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  vote = (reqObj: VoteReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new VoteReq();
      req.setUserId(reqObj.userId);
      req.setPostId(reqObj.postId);
      const client = this.getClient();
      client.vote(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  removeVote = (reqObj: RemoveVoteReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new RemoveVoteReq();
      req.setUserId(reqObj.userId);
      req.setPostId(reqObj.postId);
      const client = this.getClient();
      client.removeVote(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  addComment = (reqObj: AddCommentReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new AddCommentReq();
      req.setUserId(reqObj.userId);
      req.setUserName(reqObj.userName);
      req.setPostId(reqObj.postId);
      req.setContent(reqObj.content);
      const client = this.getClient();
      client.addComment(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  listComments = (reqObj: ListCommentsReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new ListCommentsReq();
      req.setUserId(reqObj.userId);
      req.setPostId(reqObj.postId);
      const client = this.getClient();
      client.listComments(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };

  deleteComment = (reqObj: DeleteCommentReq.AsObject) => {
    return new Promise((resolve, reject) => {
      const req = new DeleteCommentReq();
      req.setUserId(reqObj.userId);
      req.setCommentId(reqObj.commentId);
      const client = this.getClient();
      client.deleteComment(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject());
      });
    });
  };
}

export class EAHostingClient {
  meta: grpc.Metadata.HeaderObject = {};
  constructor() {
    this.meta = {};
  }
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (__eaHostingProtoClient != null) {
      return __eaHostingProtoClient;
    }
    __eaHostingProtoClient = new EAHostingServiceProtoClient(gatewayAddress);
    return __eaHostingProtoClient;
  };

  uploadEA = async (file: File, platform: string): Promise<UploadEAResponse.AsObject> => {
    const buf = new Uint8Array(await file.arrayBuffer());
    return new Promise((resolve, reject) => {
      const req = new UploadEARequest();
      req.setContent(buf);
      req.setFilename(file.name);
      req.setPlatform(platform);
      const client = this.getClient();
      client.uploadEA(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as UploadEAResponse.AsObject);
      });
    });
  };

  uploadSettingsSet = (eaArtifactId: string, setBytes: Uint8Array): Promise<SettingsSchemaResult.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new UploadSettingsSetRequest();
      req.setEaArtifactId(eaArtifactId);
      req.setSetBytes(setBytes);
      const client = this.getClient();
      client.uploadSettingsSet(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as SettingsSchemaResult.AsObject);
      });
    });
  };

  startSetupJob = (eaArtifactId: string): Promise<StartSetupJobResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new StartSetupJobRequest();
      req.setEaArtifactId(eaArtifactId);
      const client = this.getClient();
      client.startSetupJob(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as StartSetupJobResponse.AsObject);
      });
    });
  };

  // Pass a sha256 during a deploy (the EA is known, the instance is not yet),
  // or an instanceId afterwards (the instance is known, its EA digest is not).
  getSettingsSchema = (arg: string | { instanceId: string }): Promise<GetSettingsSchemaResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GetSettingsSchemaRequest();
      if (typeof arg === 'string') req.setSha256(arg);
      else req.setInstanceId(arg.instanceId);
      const client = this.getClient();
      client.getSettingsSchema(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as GetSettingsSchemaResponse.AsObject);
      });
    });
  };

  createBrokerProfile = (input: {
    userId: string;
    displayName: string;
    broker: string;
    server: string;
    accountLogin: number;
    password: string;
    isDemo: boolean;
    // "MT4" | "MT5" — the engine vaults credentials per platform.
    platform: string;
  }): Promise<BrokerProfile.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new CreateBrokerProfileRequest();
      req.setUserId(input.userId);
      req.setDisplayName(input.displayName);
      req.setBroker(input.broker);
      req.setServer(input.server);
      req.setAccountLogin(input.accountLogin);
      req.setPassword(input.password);
      req.setIsDemo(input.isDemo);
      req.setPlatform(input.platform);
      const client = this.getClient();
      client.createBrokerProfile(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as BrokerProfile.AsObject);
      });
    });
  };

  listBrokerProfiles = (userId: string, platform = ''): Promise<ListBrokerProfilesResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new ListBrokerProfilesRequest();
      req.setUserId(userId);
      if (platform) req.setPlatform(platform);
      const client = this.getClient();
      client.listBrokerProfiles(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as ListBrokerProfilesResponse.AsObject);
      });
    });
  };

  // Never blocks: returns "checking" while a login runs (poll again). recheck asks for a fresh check.
  validateBrokerLogin = (id: string, recheck = false): Promise<ValidateBrokerLoginResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new ValidateBrokerLoginRequest();
      req.setId(id);
      req.setRecheck(recheck);
      const client = this.getClient();
      client.validateBrokerLogin(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as ValidateBrokerLoginResponse.AsObject);
      });
    });
  };

  // The account's stored symbol list; never starts a check.
  getBrokerProfileSymbols = (id: string): Promise<GetBrokerProfileSymbolsResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GetBrokerProfileSymbolsRequest();
      req.setId(id);
      const client = this.getClient();
      client.getBrokerProfileSymbols(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as GetBrokerProfileSymbolsResponse.AsObject);
      });
    });
  };

  // Omitted fields stay unchanged. Account fields fail with FAILED_PRECONDITION while an EA uses the profile.
  updateBrokerProfile = (input: {
    id: string;
    displayName?: string;
    password?: string;
    broker?: string;
    server?: string;
    accountLogin?: number;
    isDemo?: boolean;
  }): Promise<BrokerProfile.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new UpdateBrokerProfileRequest();
      req.setId(input.id);
      if (input.displayName) req.setDisplayName(input.displayName);
      if (input.password) req.setPassword(input.password);
      if (input.broker) req.setBroker(input.broker);
      if (input.server) req.setServer(input.server);
      if (input.accountLogin) req.setAccountLogin(input.accountLogin);
      if (input.isDemo !== undefined) req.setIsDemo(input.isDemo);
      const client = this.getClient();
      client.updateBrokerProfile(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as BrokerProfile.AsObject);
      });
    });
  };

  // Fails with FAILED_PRECONDITION while an instance still uses the profile.
  deleteBrokerProfile = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new DeleteBrokerProfileRequest();
      req.setId(id);
      const client = this.getClient();
      client.deleteBrokerProfile(req, this.meta as any, (err) => {
        if (err != null) reject(err);
        else resolve();
      });
    });
  };

  createInstance = (input: {
    userId: string;
    eaArtifactId: string;
    brokerProfileId: string;
    name: string;
    symbol: string;
    timeframe: string;
    settings: { [key: string]: any };
    tier?: string;
    idempotencyKey?: string;
    allowDllImport?: boolean;
  }): Promise<Instance.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new CreateInstanceRequest();
      req.setUserId(input.userId);
      req.setEaArtifactId(input.eaArtifactId);
      req.setBrokerProfileId(input.brokerProfileId);
      req.setName(input.name);
      req.setSymbol(input.symbol);
      req.setTimeframe(input.timeframe);
      req.setSettings(Struct.fromJavaScript(input.settings));
      if (input.tier) req.setTier(input.tier);
      if (input.idempotencyKey) req.setIdempotencyKey(input.idempotencyKey);
      if (input.allowDllImport) req.setAllowDllImport(true);
      const client = this.getClient();
      client.createInstance(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as Instance.AsObject);
      });
    });
  };

  listInstances = (userId: string, status?: string): Promise<ListInstancesResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new ListInstancesRequest();
      req.setUserId(userId);
      if (status) req.setStatus(status);
      const client = this.getClient();
      client.listInstances(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as ListInstancesResponse.AsObject);
      });
    });
  };

  getInstanceStatus = (instanceId: string): Promise<InstanceStatus.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GetInstanceStatusRequest();
      req.setInstanceId(instanceId);
      const client = this.getClient();
      client.getInstanceStatus(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as InstanceStatus.AsObject);
      });
    });
  };

  // Latest in-terminal account snapshot (balance/equity/floating P&L). Rejects with
  // NotFound until the first heartbeat with metrics lands — callers treat as absent.
  getInstanceMetrics = (instanceId: string): Promise<InstanceMetrics.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GetInstanceMetricsRequest();
      req.setInstanceId(instanceId);
      const client = this.getClient();
      client.getInstanceMetrics(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as InstanceMetrics.AsObject);
      });
    });
  };

  listEquityHistory = (instanceId: string, hours = 24): Promise<ListEquityHistoryResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new ListEquityHistoryRequest();
      req.setInstanceId(instanceId);
      req.setHours(hours);
      const client = this.getClient();
      client.listEquityHistory(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as ListEquityHistoryResponse.AsObject);
      });
    });
  };

  // The slot's real MT5 terminal/expert journal tail, proxied via the control plane
  // from the assigned worker agent. note is set when no journal is available.
  getInstanceJournal = (instanceId: string, tailLines = 0): Promise<GetInstanceJournalResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GetInstanceJournalRequest();
      req.setInstanceId(instanceId);
      if (tailLines > 0) req.setTailLines(tailLines);
      const client = this.getClient();
      client.getInstanceJournal(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as GetInstanceJournalResponse.AsObject);
      });
    });
  };

  startInstance = (id: string): Promise<Instance.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new StartInstanceRequest();
      req.setId(id);
      const client = this.getClient();
      client.startInstance(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as Instance.AsObject);
      });
    });
  };

  stopInstance = (id: string): Promise<Instance.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new StopInstanceRequest();
      req.setId(id);
      const client = this.getClient();
      client.stopInstance(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as Instance.AsObject);
      });
    });
  };

  restartInstance = (id: string): Promise<Instance.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new RestartInstanceRequest();
      req.setId(id);
      const client = this.getClient();
      client.restartInstance(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as Instance.AsObject);
      });
    });
  };

  // Creates a new pending settings version; the instance reconciler hot-applies it.
  // previousVersion is optimistic concurrency: must equal the instance's current version.
  updateSettings = (input: {
    instanceId: string;
    previousVersion: number;
    settings: { [key: string]: any };
    appliedByUser?: string;
  }): Promise<{ version: number; status: string; applyError: string }> => {
    return new Promise((resolve, reject) => {
      const req = new UpdateSettingsRequest();
      req.setInstanceId(input.instanceId);
      req.setPreviousVersion(input.previousVersion);
      req.setSettings(Struct.fromJavaScript(input.settings));
      if (input.appliedByUser) req.setAppliedByUser(input.appliedByUser);
      const client = this.getClient();
      client.updateSettings(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve({ version: resp!.getVersion(), status: resp!.getStatus(), applyError: resp!.getApplyError() });
      });
    });
  };

  // Roll an instance back to a previously applied settings version. The engine
  // inserts it as a NEW pending version (history stays append-only) and the
  // reconciler applies it the same way as any other — so the bot restarts once
  // and the rollback is auditable rather than a silent overwrite.
  rollbackSettings = (input: {
    instanceId: string;
    toVersion: number;
    appliedBy?: string;
  }): Promise<{ version: number; status: string; applyError: string }> => {
    return new Promise((resolve, reject) => {
      const req = new RollbackSettingsRequest();
      req.setInstanceId(input.instanceId);
      req.setToVersion(input.toVersion);
      if (input.appliedBy) req.setAppliedByUser(input.appliedBy);
      const client = this.getClient();
      client.rollbackSettings(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve({ version: resp!.getVersion(), status: resp!.getStatus(), applyError: resp!.getApplyError() });
      });
    });
  };

  // Settings values arrive as google.protobuf.Struct; convert to plain objects here
  // (toObject() would expose the awkward fieldsMap encoding).
  listSettingsVersions = (
    instanceId: string
  ): Promise<Array<{ version: number; status: string; applyError: string; values: { [key: string]: any } }>> => {
    return new Promise((resolve, reject) => {
      const req = new ListSettingsVersionsRequest();
      req.setInstanceId(instanceId);
      const client = this.getClient();
      client.listSettingsVersions(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else
          resolve(
            (resp?.getVersionsList() || []).map((v) => ({
              version: v.getVersion(),
              status: v.getStatus(),
              applyError: v.getApplyError(),
              values: (v.getValues()?.toJavaScript() as { [key: string]: any }) || {}
            }))
          );
      });
    });
  };

  deleteInstance = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new DeleteInstanceRequest();
      req.setId(id);
      const client = this.getClient();
      client.deleteInstance(req, this.meta as any, (err) => {
        if (err != null) reject(err);
        else resolve();
      });
    });
  };

  // Slot entitlement + suspension state, served by ea-manager (synced from
  // billing). status: 'none' | 'active' | 'suspended'.
  getHostingOverview = (userId: string): Promise<HostingOverview.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GetHostingOverviewRequest();
      req.setUserId(userId);
      const client = this.getClient();
      client.getHostingOverview(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as HostingOverview.AsObject);
      });
    });
  };

  // Upload tracker: extraction progress persisted server-side (any session).
  listUploads = (userId: string): Promise<ListUploadsResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new ListUploadsRequest();
      req.setUserId(userId);
      const client = this.getClient();
      client.listUploads(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as ListUploadsResponse.AsObject);
      });
    });
  };

  dismissUpload = (userId: string, sha256: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const req = new DismissUploadRequest();
      req.setUserId(userId);
      req.setSha256(sha256);
      const client = this.getClient();
      client.dismissUpload(req, this.meta as any, (err) => {
        if (err != null) reject(err);
        else resolve();
      });
    });
  };
}

export class AchievementClient {
  meta: grpc.Metadata.HeaderObject = {};
  setMeta(key: string, value: string | string[]) {
    this.meta[key] = value;
  }
  getClient = () => {
    if (!__achievementClient) __achievementClient = new AchievementManagerClient(gatewayAddress);
    return __achievementClient;
  };

  generateCertificate = (reqObj: {
    userId: string;
    timeRange: TimeRangeMap[keyof TimeRangeMap];
    accountIds: string[];
  }): Promise<GenerateCertificateReply.AsObject> => {
    return new Promise((resolve, reject) => {
      const req = new GenerateCertificateReq();
      req.setUserId(reqObj.userId);
      req.setTimeRange(reqObj.timeRange);
      req.setAccountIdsList(reqObj.accountIds);
      this.getClient().generateCertificate(req, this.meta as any, (err, resp) => {
        if (err != null) reject(err);
        else resolve(resp?.toObject() as GenerateCertificateReply.AsObject);
      });
    });
  };
}
